// src/views/Invoices.jsx
import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import Split from 'react-split';
import './Invoices.css';
import { showToast, hideToast, updateToast } from '../utils/toast';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  FaEdit, FaFilePdf, FaSync, FaUndo, FaBan, FaBomb, FaChevronDown, FaTimes, FaSearch,
  FaColumns, FaSave, FaDollarSign, FaEnvelope, FaEnvelopeOpenText, FaPrint, FaCopy,
  FaPlus, FaDownload, FaFileCsv, FaEye, FaPaperclip, FaUpload, FaHistory, FaMoneyBillWave
} from 'react-icons/fa';
import EmailModal from '../components/EmailModal.jsx';

const API = import.meta.env.VITE_API_URL;
const DEFAULT_LIMIT = 50;
const STORAGE_KEY = 'invoices.state.v2';
const PRESETS_KEY = 'invoices.presets.v1';
const COLUMNS_KEY = 'invoices.columns.v1';
const LS_INV_COLWIDTHS_KEY = 'invoicesColWidths.v1';
const MIN_COL_W = 40;

// ---------- role helpers ----------
function getCurrentUser() {
  try { return JSON.parse(localStorage.getItem('user') || '{}') || {}; }
  catch { return {}; }
}
function isClientUser(u) {
  const v = u.isclient ?? u.isClient ?? u.role ?? u.roleid;
  if (v === true) return true;
  if (typeof v === 'number') return v === 1;
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    return s === '1' || s === 'true' || s === 'yes' || s === 'y' || s === 'client';
  }
  return false;
}
function getUserClientId(u) { return (u.clientid ?? u.clientId ?? u.client_id ?? '') ? String(u.clientid ?? u.clientId ?? u.client_id) : ''; }
// -----------------------------------

const PAYMENTS_INDEX_ROUTE = '/payments';
const PAYMENTS_FILTER_PARAM_LO = 'invoiceid';
const PAYMENTS_FILTER_PARAM_CC = 'invoiceId';

const USD = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
const money = (v) => USD.format(Number.isFinite(+v) ? +v : 0);

// Helper for simple date rendering (YYYY-MM-DD)
const dateOnly = (v) => {
  if (!v) return '';
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0,10);
  const d = new Date(s); return isNaN(d.getTime()) ? s : d.toISOString().slice(0,10);
};

const formatDateTime = (v) => {
  if (!v) return '';
  const d = new Date(v);
  if (!Number.isNaN(d.getTime())) {
    return d.toLocaleString();
  }
  return String(v);
};

const pdfTitleForType = (typeName) => {
  const raw = (typeName || '').toString().trim();
console.log( "typename = " + typeName );

  const t = raw.toLowerCase();
  const rules = [
    { keys: ['monthly storage', 'warehouse storage', 'storage', 'pallet storage'], title: 'Monthly Warehouse Storage Invoice' },
    { keys: ['receiv', 'inbound'], title: 'Receiving Invoice' },
    { keys: ['handling', 'wh handling'], title: 'Handling Charges Invoice' },
    { keys: ['outbound', 'ship', 'shipping', 'fulfill', 'pick', 'pack'], title: 'Outbound Shipping Invoice' },
    { keys: ['labor', 'hour'], title: 'Labor Invoice' },
    { keys: ['rent'], title: 'Warehouse Rent Invoice' },
    { keys: ['container', 'demurrage', 'detention'], title: 'Container / Demurrage Invoice' },
    { keys: ['misc', 'other', 'surcharge', 'fee'], title: 'Miscellaneous Charges Invoice' },
    { keys: ['monthly'], title: 'Monthly Warehouse Invoice' },
  ];
  for (const r of rules) {
    if (r.keys.some(k => t.includes(k)))
       return r.title;
  }
console.log( "raw = " + raw );
  if (raw) {
    console.log( "raw" );  
    // Fallback: prettify original type name and append "Invoice"
    const pretty = raw
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, c => c.toUpperCase());
    return `${pretty} Invoice`;
  }
  return '';
};

function getUserFullName() {
  try {
    const u =
      JSON.parse(localStorage.getItem('currentUser')) ||
      JSON.parse(localStorage.getItem('user')) || {};
    return u.fullname || u.userFullName || u.name || '';
  } catch { return ''; }
}

// ---- localStorage helpers ----
function loadPersisted() { try { const raw = localStorage.getItem(STORAGE_KEY); return raw ? JSON.parse(raw) : {}; } catch { return {}; } }
function savePersisted(next) { try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch {} }
function loadPresets() { try { const raw = localStorage.getItem(PRESETS_KEY); return raw ? JSON.parse(raw) : {}; } catch { return {}; } }
function savePresets(obj) { try { localStorage.setItem(PRESETS_KEY, JSON.stringify(obj)); } catch {} }
function loadColumns() { try { const raw = localStorage.getItem(COLUMNS_KEY); return raw ? JSON.parse(raw) : null; } catch { return null; } }
function saveColumns(cols) { try { localStorage.setItem(COLUMNS_KEY, JSON.stringify(cols)); } catch {} }

/** Lightweight searchable multi-select (status filter) */
function MultiSelectStatuses({ options = [], value = [], onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function onDocClick(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    function onKey(e) { if (e.key === 'Escape') setOpen(false); }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  const isAll = value.length === 0;
  const toggle = (id) => {
    const sid = String(id);
    if (value.includes(sid)) onChange(value.filter(v => v !== sid));
    else onChange([...value, sid]);
  };
  const clear = () => onChange([]);
  const selectAll = () => onChange(options.map(o => String(o.id)));

  const selectedNames = options.filter(o => value.includes(String(o.id))).map(o => o.name);
  const summary = isAll ? 'All statuses'
    : selectedNames.length <= 2 ? selectedNames.join(', ')
    : `${selectedNames.slice(0,2).join(', ')} +${selectedNames.length - 2}`;

  return (
    <div className="ms-container" ref={ref}>
      <button type="button" className={`ms-control ${open ? 'open' : ''}`} onClick={() => setOpen(v => !v)}
        aria-haspopup="listbox" aria-expanded={open} title="Filter by status">
        <span className={`ms-summary ${isAll ? 'muted' : ''}`}>{summary}</span>
        {!isAll && (
          <span className="ms-clear" onClick={(e) => { e.stopPropagation(); clear(); }} title="Clear" aria-label="Clear">×</span>
        )}
        <span className="ms-caret">▾</span>
      </button>

      {open && (
        <div className="ms-popover">
          <div className="ms-tools">
            <div className="ms-actions">
              <button type="button" className="btn" onClick={selectAll}>Select All</button>
              <button type="button" className="btn" onClick={clear}>Clear</button>
            </div>
          </div>
          <ul className="ms-list" role="listbox" aria-multiselectable="true">
            {options.length ? options.map(o => {
              const checked = value.includes(String(o.id));
              return (
                <li key={o.id}>
                  <label className="ms-option">
                    <input type="checkbox" checked={checked} onChange={() => toggle(o.id)} />
                    <span>{o.name}</span>
                  </label>
                </li>
              );
            }) : <li className="ms-empty">No statuses</li>}
          </ul>
        </div>
      )}
    </div>
  );
}

// MultiSelectTypes: similar to MultiSelectStatuses, but for Type
function MultiSelectTypes({ options = [], value = [], onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function onDocClick(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    function onKey(e) { if (e.key === 'Escape') setOpen(false); }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  const isAll = (value || []).length === 0;
  const toggle = (id) => {
    const sid = String(id);
    if (value.includes(sid)) onChange(value.filter(v => v !== sid));
    else onChange([...(value || []), sid]);
  };
  const clear = () => onChange([]);
  const selectAll = () => onChange(options.map(o => String(o.id)));

  const selectedNames = options.filter(o => value.includes(String(o.id))).map(o => o.name);
  const summary = isAll ? 'All types' : (selectedNames.length <= 2 ? selectedNames.join(', ') : `${selectedNames.slice(0,2).join(', ')} +${selectedNames.length - 2}`);

  return (
    <div className="ms-container" ref={ref}>
      <button type="button" className={`ms-control ${open ? 'open' : ''}`} onClick={() => setOpen(v => !v)}
        aria-haspopup="listbox" aria-expanded={open} title="Filter by type">
        <span className={`ms-summary ${isAll ? 'muted' : ''}`}>{summary}</span>
        {!isAll && (
          <span className="ms-clear" onClick={(e) => { e.stopPropagation(); clear(); }} title="Clear" aria-label="Clear">×</span>
        )}
        <span className="ms-caret">▾</span>
      </button>
      {open && (
        <div className="ms-popover">
          <div className="ms-tools">
            <div className="ms-actions">
              <button type="button" className="btn" onClick={selectAll}>Select All</button>
              <button type="button" className="btn" onClick={clear}>Clear</button>
            </div>
          </div>
          <ul className="ms-list" role="listbox" aria-multiselectable="true">
            {options.length ? options.map(o => {
              const checked = value.includes(String(o.id));
              return (
                <li key={o.id}>
                  <label className="ms-option">
                    <input type="checkbox" checked={checked} onChange={() => toggle(o.id)} />
                    <span>{o.name}</span>
                  </label>
                </li>
              );
            }) : <li className="ms-empty">No types</li>}
          </ul>
        </div>
      )}
    </div>
  );
}

function PaymentsPopover({ open, anchorRect, onClose, payments = [], invoiceId, onMouseEnter, onMouseLeave }) {
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    function onDoc(e) {
      if (!open) return;
      if (e.key === 'Escape') onClose?.();
      if (ref.current && !ref.current.contains(e.target)) {
        if (e.type === 'mousedown') onClose?.();
      }
    }
    document.addEventListener('keydown', onDoc);
    document.addEventListener('mousedown', onDoc);
    return () => {
      document.removeEventListener('keydown', onDoc);
      document.removeEventListener('mousedown', onDoc);
    };
  }, [open, onClose]);

  if (!open || !anchorRect) return null;

  const style = {
    position: 'fixed',
    top: Math.min(anchorRect.bottom + 6, window.innerHeight - 260),
    left: Math.max(Math.min(anchorRect.left, window.innerWidth - 420), 10),
    zIndex: 2000,
    width: 400,
    maxWidth: '90vw',
    maxHeight: 240,
    overflow: 'auto',
    background: '#ffffff',
    color: '#000000',
    border: '1px solid #cccccc',
    borderRadius: '8px',
    boxShadow: '0 8px 24px rgba(0,0,0,.25)',
  };

  const gotoPaymentsFiltered = () => {
    const qs = new URLSearchParams();
    qs.set(PAYMENTS_FILTER_PARAM_LO, String(invoiceId));
    qs.set(PAYMENTS_FILTER_PARAM_CC, String(invoiceId));
    navigate({ pathname: PAYMENTS_INDEX_ROUTE, search: `?${qs.toString()}` });
    onClose?.();
  };

  return (
    <div
      className="payments-popover"
      ref={ref}
      style={style}
      role="dialog"
      aria-label={`Payments for invoice #${invoiceId}`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'8px 10px', borderBottom:'1px solid #ddd', position:'sticky', top:0, background:'#ffffff', color:'#000000', zIndex:1 }}>
        <strong>Payments for #{invoiceId}</strong>
        <div style={{ display:'flex', gap:8 }}>
          <button className="mini-btn" style={{ color:'#000' }} onClick={gotoPaymentsFiltered} title="Open Payments filtered by this invoice">Open list</button>
          <button className="mini-btn" style={{ color:'#000' }} onClick={onClose}>Close</button>
        </div>
      </div>
      <div style={{ padding: '8px 10px' }}>
        {Array.isArray(payments) && payments.length > 0 ? (
          <table style={{ width:'100%', borderCollapse:'collapse', color:'#000' }}>
            <thead>
              <tr>
                <th style={{ textAlign:'left', borderBottom:'1px solid #444', padding:'6px 4px', color:'#000' }}>ID</th>
                <th style={{ textAlign:'left', borderBottom:'1px solid #444', padding:'6px 4px', color:'#000' }}>Date</th>
                <th style={{ textAlign:'left', borderBottom:'1px solid #444', padding:'6px 4px', color:'#000' }}>Type</th>
                <th style={{ textAlign:'left', borderBottom:'1px solid #444', padding:'6px 4px', color:'#000' }}>Reference</th>
                <th style={{ textAlign:'right', borderBottom:'1px solid #444', padding:'6px 4px', color:'#000' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {payments.map(p => {
                const onKey = (e) => { if (e.key === 'Enter') gotoPaymentsFiltered(); };
                return (
                  <tr
                    key={p.id}
                    onClick={gotoPaymentsFiltered}
                    onKeyDown={onKey}
                    tabIndex={0}
                    title={`View payments for invoice #${invoiceId}`}
                    style={{ cursor:'pointer' }}
                  >
                    <td style={{ padding:'6px 4px', borderBottom:'1px solid #333', color:'#000' }}>{p.id}</td>
                    <td style={{ padding:'6px 4px', borderBottom:'1px solid #333', color:'#000' }}>
                      {p.date ? new Date(p.date).toLocaleDateString() : ''}
                    </td>
                    <td style={{ padding:'6px 4px', borderBottom:'1px solid #333', color:'#000' }}>{p.paymentTypeName || ''}</td>
                    <td style={{ padding:'6px 4px', borderBottom:'1px solid #333', color:'#000' }}>{p.reference || ''}</td>
                    <td style={{ padding:'6px 4px', borderBottom:'1px solid #333', textAlign:'right', color:'#000' }}>
                      {money(p.amt)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div style={{ opacity:.8 }}>No payments</div>
        )}
      </div>
    </div>
  );
}

const COLUMN_DEFS = [
  { key: 'invoiceId', label: 'Invoice #', sort: 'invoiceId' },
  { key: 'status', label: 'Status', sort: 'status' },
  { key: 'clientName', label: 'Client', sort: 'clientName' },
  { key: 'invoiceDate', label: 'Date', sort: 'invoiceDate' },
  { key: 'invoiceType', label: 'Type', sort: 'invoiceType' },
  // New: Associated document reference (WR/Order/E-Order)
  { key: 'docRef', label: 'Doc #', sort: null },
  { key: 'totalAmount', label: 'Total', sort: 'totalAmount', cls: 'num' },
  { key: 'amountPaid', label: 'Paid', sort: 'amountPaid', cls: 'num' },
  { key: 'amountDue', label: 'Due', sort: 'amountDue', cls: 'num' },
  { key: 'paymentCount', label: 'Payments', sort: 'paymentCount' },
  { key: 'lastPaymentDate', label: 'Last Payment', sort: 'lastPaymentDate' },
  { key: 'createdBy', label: 'Created By', sort: null },
  { key: 'notes', label: 'Notes', sort: null },
];
const DEFAULT_VISIBLE = COLUMN_DEFS.map(c => c.key);

// ---- Date preset helpers ----
function ymdLocal(d) {
  const dt = new Date(d);
  dt.setMinutes(dt.getMinutes() - dt.getTimezoneOffset());
  return dt.toISOString().slice(0, 10);
}
function firstOfMonth(date = new Date()) {
  const d = new Date(date.getFullYear(), date.getMonth(), 1);
  return ymdLocal(d);
}
function lastOfMonth(date = new Date()) {
  const d = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  return ymdLocal(d);
}
function addDays(date, delta) {
  const d = new Date(date);
  d.setDate(d.getDate() + delta);
  return d;
}
// Week helpers
function startOfWeek(date = new Date()) {
  const d = new Date(date);
  const day = d.getDay(); // 0 = Sun
  const diff = (day === 0 ? -6 : 1 - day); // Monday as first day
  d.setDate(d.getDate() + diff);
  return ymdLocal(d);
}
function endOfWeek(date = new Date()) {
  const d = new Date(date);
  const day = d.getDay(); // 0 = Sun
  const diff = (day === 0 ? -6 : 1 - day); // Monday as first day
  d.setDate(d.getDate() + diff + 6); // Sunday end
  return ymdLocal(d);
}

const Invoices = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // role lock
  const user = getCurrentUser();
  const IS_CLIENT = isClientUser(user);
  const CLIENT_ID = getUserClientId(user);
  // Client filter for client users (list of allowed clients via reports/filters/clients)
  const [clientOptions, setClientOptions] = useState([]);
  const [filteredClientOptions, setFilteredClientOptions] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(() => {
    try { return localStorage.getItem('invoicesClientId') || ''; } catch { return ''; }
  });
  const singleClientOption = useMemo(
    () => (IS_CLIENT && clientOptions.length === 1 ? clientOptions[0] : null),
    [IS_CLIENT, clientOptions]
  );
  useEffect(() => { try { localStorage.setItem('invoicesClientId', selectedClientId || ''); } catch {} }, [selectedClientId]);
  useEffect(() => {
    const loadClients = async () => {
      try {
        // Client users may not have reports access; use the minimal clients list instead.
        const res = await fetch(`${API}/api/clients/minimal`);
        const data = res.ok ? await res.json() : [];
        const opts = (Array.isArray(data) ? data : []).map(c => ({ id: String(c.id), name: c.name }));
        setClientOptions(opts);
        setFilteredClientOptions(opts);
        // keep selection if still valid; else clear
        if (selectedClientId && !opts.find(o => String(o.id) === String(selectedClientId))) setSelectedClientId('');
        if (opts.length === 1) {
          setSelectedClientId(opts[0].id);
        }
      } catch { setClientOptions([]); }
    };
    if (IS_CLIENT) loadClients();
  }, [IS_CLIENT]);

  // For internal users, load full client list for typeahead suggestions
  useEffect(() => {
    if (IS_CLIENT) return;
    const loadClientsInternal = async () => {
      try {
        const res = await fetch(`${API}/api/clients?limit=1000`);
        const json = await res.json();
        const opts = Array.isArray(json?.data)
          ? json.data.map(c => ({ id: String(c.id), name: c.name }))
          : [];
        setClientOptions(opts);
        setFilteredClientOptions(opts);
      } catch {
        setClientOptions([]);
        setFilteredClientOptions([]);
      }
    };
    loadClientsInternal();
  }, [IS_CLIENT]);

  const persisted = loadPersisted();

  const [sortField, setSortField] = useState(persisted.sortField || 'invoiceDate');
  const [sortOrder, setSortOrder] = useState(persisted.sortOrder || 'DESC');

  const [statusIds, setStatusIds] = useState(
    Array.isArray(persisted.statusIds) ? persisted.statusIds.map(String) : []
  );
  const [from, setFrom] = useState(persisted.from || '');
  const [to, setTo] = useState(persisted.to || '');
  const [q, setQ] = useState(typeof persisted.q === 'string' ? persisted.q : '');
  const [datePreset, setDatePreset] = useState(persisted.datePreset || '');
  const [balanceFilter, setBalanceFilter] = useState(
    persisted.balanceFilter === 'open' ? 'open' : 'all'
  );

  const initialLimit = Number.isFinite(+persisted.limit) ? +persisted.limit : DEFAULT_LIMIT;
  const initialOffset = Number.isFinite(+persisted.offset) ? Math.max(0, +persisted.offset) : 0;

  const [data, setData] = useState([]);
  const [meta, setMeta] = useState({
    total: 0,
    limit: initialLimit,
    offset: initialOffset,
    totals: { totalAmount: 0, amountPaid: 0, amountDue: 0 },
  });

  const [isTypingLimit, setIsTypingLimit] = useState(false);
  const [limitInput, setLimitInput] = useState(String(initialLimit));
  useEffect(() => { setLimitInput(String(meta.limit || DEFAULT_LIMIT)); }, [meta.limit]);

  const [statuses, setStatuses] = useState([]);
  const [types, setTypes] = useState([]); // [{id, name}]
  const [typeIds, setTypeIds] = useState(Array.isArray(persisted.typeIds) ? persisted.typeIds.map(String) : []);
  const [loading, setLoading] = useState(false);
  // Separate client name filter (internal users)
  const getInitialClientName = () => {
    try { return localStorage.getItem('invoicesClientName') || ''; } catch { return ''; }
  };
  const [clientNameFilter, setClientNameFilter] = useState(getInitialClientName());
  useEffect(() => { try { localStorage.setItem('invoicesClientName', clientNameFilter || ''); } catch {} }, [clientNameFilter]);

  const parseClientIdFromClientFilter = useCallback((raw) => {
    const s = String(raw || '').trim();
    if (!s) return '';
    // Allow formats like: "Warp (#123)", "(#123)", "#123"
    const m = s.match(/(?:\(#\s*(\d+)\)|#\s*(\d+))\s*$/);
    const id = m ? (m[1] || m[2]) : '';
    return id && /^\d+$/.test(id) ? id : '';
  }, []);

  const resolvedInternalClientId = useMemo(() => {
    if (IS_CLIENT) return '';
    const explicit = parseClientIdFromClientFilter(clientNameFilter);
    if (explicit) return explicit;
    const term = String(clientNameFilter || '').trim().toLowerCase();
    if (!term) return '';
    const matches = clientOptions.filter(o => String(o?.name || '').trim().toLowerCase() === term);
    return matches.length === 1 ? String(matches[0].id) : '';
  }, [IS_CLIENT, clientNameFilter, clientOptions, parseClientIdFromClientFilter]);

  // Keep filtered client list in sync with typed client name (for suggestions)
  useEffect(() => {
    const term = (clientNameFilter || '').trim().toLowerCase();
    // When no term, show only a small leading slice to keep the datalist light
    if (!term) {
      setFilteredClientOptions(clientOptions.slice(0, 50));
      return;
    }
    // For performance, cap suggestions and avoid scanning unnecessarily large arrays
    const maxResults = 50;
    const out = [];
    for (const opt of clientOptions) {
      const name = (opt.name || '').toLowerCase();
      if (name.includes(term)) {
        out.push(opt);
        if (out.length >= maxResults) break;
      }
    }
    setFilteredClientOptions(out);
  }, [clientNameFilter, clientOptions]);
  // View Invoice modal state
  const [viewOpen, setViewOpen] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewData, setViewData] = useState(null);

  // Bottom pane: Attachments
  const [invBottomPaneMode, setInvBottomPaneMode] = useState(null); // 'attachments' | null
  const [invAttachments, setInvAttachments] = useState([]);
  const [invAttachmentsLoading, setInvAttachmentsLoading] = useState(false);
  const [attachDesc, setAttachDesc] = useState('');
  const [pendingFiles, setPendingFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const [invSplitSizes, setInvSplitSizes] = useState(() => {
    try { return JSON.parse(localStorage.getItem('invoices-split-sizes') || '[60,40]'); }
    catch { return [60, 40]; }
  });

  // Confirm void modal
  const [voidConfirm, setVoidConfirm] = useState({ open: false, invoiceId: null });
  const openVoidConfirm = (id) => setVoidConfirm({ open: true, invoiceId: id });
  const closeVoidConfirm = () => setVoidConfirm({ open: false, invoiceId: null });
  const confirmVoid = async () => {
    const id = voidConfirm.invoiceId;
    closeVoidConfirm();
    if (!id) return;
    await changeStatus(id, 6);
  };
  const openViewInvoice = async (invoiceId) => {
    setViewOpen(true);
    setViewLoading(true);
    setViewData(null);
    try {
      const res = await fetch(`${API}/api/invoices/${invoiceId}`);
      const j = await res.json();
      setViewData(j || null);
    } catch {
      setViewData(null);
    } finally {
      setViewLoading(false);
    }
  };

  // Attachments helpers
  const fetchInvoiceAttachments = async (invoiceId) => {
    if (!invoiceId) { setInvAttachments([]); return; }
    setInvAttachmentsLoading(true);
    try {
      const res = await fetch(`${API}/api/invoices/${invoiceId}/attachments`);
      const j = res.ok ? await res.json() : [];
      setInvAttachments(Array.isArray(j) ? j : []);
    } catch {
      setInvAttachments([]);
    } finally {
      setInvAttachmentsLoading(false);
    }
  };

  const uploadAttachmentFiles = async () => {
    if (!activeId || !pendingFiles.length) return;
    const fd = new FormData();
    pendingFiles.forEach(f => fd.append('files', f));
    if (attachDesc) fd.append('description', attachDesc);
    try {
      const res = await fetch(`${API}/api/invoices/${activeId}/attachments`, { method: 'POST', body: fd });
      if (!res.ok) throw new Error('Upload failed');
      setAttachDesc(''); setPendingFiles([]);
      await fetchInvoiceAttachments(activeId);
    } catch (e) {
      alert('Upload failed: ' + (e?.message || ''));
    }
  };

  const deleteInvoiceAttachment = async (attId) => {
    if (!activeId || !attId) return;
    if (!window.confirm('Delete this attachment?')) return;
    try {
      await fetch(`${API}/api/invoices/${activeId}/attachments/${attId}`, { method: 'DELETE' });
      await fetchInvoiceAttachments(activeId);
    } catch (e) {
      alert('Delete failed: ' + (e?.message || ''));
    }
  };

  // Invoice history modal helpers
  const [historyModal, setHistoryModal] = useState({ open: false, invoiceId: null, loading: false, entries: [] });
  const closeInvoiceHistory = () => setHistoryModal({ open: false, invoiceId: null, loading: false, entries: [] });
  const openInvoiceHistory = async (invoiceId) => {
    if (!invoiceId) return;
    setHistoryModal({ open: true, invoiceId, loading: true, entries: [] });
    try {
      const res = await fetch(`${API}/api/invoices/${invoiceId}/history`);
      const json = res.ok ? await res.json() : [];
      setHistoryModal(prev => {
        if (!prev.open || prev.invoiceId !== invoiceId) return prev;
        return { ...prev, loading: false, entries: Array.isArray(json) ? json : [] };
      });
    } catch {
      setHistoryModal(prev => {
        if (!prev.open || prev.invoiceId !== invoiceId) return prev;
        return { ...prev, loading: false, entries: [] };
      });
    }
  };
  // Centered topbar toast; on completion, report count and keep a moment
  const prevLoadingRef = useRef(false);
  useEffect(() => {
    const id = 'loading.invoices';
    if (loading && !prevLoadingRef.current) {
      showToast({ id, message: 'Loading invoices…', duration: 0, align: 'center', slideFrom: 'top', pill: true });
    }
    if (!loading && prevLoadingRef.current) {
      const total = Number((meta && meta.total) || 0);
      const count = Number.isFinite(total) && total > 0 ? total : (Array.isArray(data) ? data.length : 0);
      const formatted = Number(count).toLocaleString('en-US');
      updateToast({ id, message: `Loaded ${formatted} invoices.` });
      const t = setTimeout(() => hideToast(id), 2500);
      return () => clearTimeout(t);
    }
    prevLoadingRef.current = loading;
  }, [loading, data, meta?.total]);
  const [selected, setSelected] = useState(new Set());

  const [paymentsOpenFor, setPaymentsOpenFor] = useState(null);
  const [paymentsAnchor, setPaymentsAnchor] = useState(null);

  const [activeId, setActiveId] = useState(null);
  const onRowClick = (row) => { setActiveId(prev => (prev === row.invoiceId ? null : row.invoiceId)); };

  const hoverTimerRef = useRef(null);
  const HOVER_CLOSE_DELAY_MS = 180;
  const cancelClose = () => { if (hoverTimerRef.current) { clearTimeout(hoverTimerRef.current); hoverTimerRef.current = null; } };
  const scheduleClose = () => { cancelClose(); hoverTimerRef.current = setTimeout(() => { setPaymentsOpenFor(null); setPaymentsAnchor(null); }, HOVER_CLOSE_DELAY_MS); };
  useEffect(() => () => { if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current); }, []);

  const openPayments = (invoiceId, anchorEl) => { const r = anchorEl?.getBoundingClientRect?.(); setPaymentsAnchor(r || null); setPaymentsOpenFor(invoiceId); };
  const closePayments = () => { setPaymentsOpenFor(null); setPaymentsAnchor(null); };

  // When attachments pane is open and selection changes, refresh list
  useEffect(() => {
    if (invBottomPaneMode === 'attachments' && activeId) fetchInvoiceAttachments(activeId);
  }, [invBottomPaneMode, activeId]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/invoices/statuses`);
        const json = await res.json();
        setStatuses((json || []).map(s => ({ id: Number(s.id), name: s.name })));
      } catch (e) { console.error('Failed to load statuses', e); }
    })();
  }, []);

  const markInvoicesAsSent = (ids) => {
    const targetIds = Array.isArray(ids) ? ids.map(Number) : [Number(ids)];
    if (!targetIds.length) return;
    const sentMeta = statuses.find(s => String(s.name || '').toLowerCase() === 'sent');
    if (!sentMeta) return;
    // Update main list
    setData(prev => {
      if (!Array.isArray(prev)) return prev;
      return prev.map(row => {
        if (!targetIds.includes(Number(row.invoiceId))) return row;
        return {
          ...row,
          statusId: sentMeta.id,
          statusName: sentMeta.name,
        };
      });
    });
    // Update View Invoice modal header if open
    setViewData(prev => {
      if (!prev || !prev.header) return prev;
      const headerId = Number(prev.header.id ?? prev.header.invoiceId);
      if (!targetIds.includes(headerId)) return prev;
      return {
        ...prev,
        header: {
          ...prev.header,
          statusId: sentMeta.id,
          statusName: sentMeta.name,
        },
      };
    });
  };

  useEffect(() => {
    savePersisted({ from, to, q, statusIds, typeIds, sortField, sortOrder, limit: meta.limit, offset: meta.offset, datePreset, balanceFilter });
  }, [from, to, q, statusIds, typeIds, sortField, sortOrder, meta.limit, meta.offset, datePreset, balanceFilter]);
  // Date preset handler
  const applyDatePreset = (val) => {
    const today = new Date();
    let f = '', t = '';
    switch (val) {
      case 'today':
        f = t = ymdLocal(today); break;
      case 'yesterday': {
        const y = addDays(today, -1);
        f = t = ymdLocal(y); break; }
      case 'thisWeek':
        f = startOfWeek(today);
        t = endOfWeek(today); break;
      case 'lastWeek': {
        const startLW = addDays(startOfWeek(today), -7); // Monday last week
        f = ymdLocal(startLW);
        t = endOfWeek(startLW); // Sunday last week
        break; }
      case 'last7':
        t = ymdLocal(today);
        f = ymdLocal(addDays(today, -6));
        break;
      case 'last30':
        t = ymdLocal(today);
        f = ymdLocal(addDays(today, -29));
        break;
      case 'last90':
        t = ymdLocal(today);
        f = ymdLocal(addDays(today, -89));
        break;
      case 'thisMonth':
        f = firstOfMonth(today);
        t = lastOfMonth(today); break;
      case 'lastMonth': {
        const firstThis = firstOfMonth(today);
        const d = new Date(firstThis + 'T00:00:00');
        const prevMonth = new Date(d.getFullYear(), d.getMonth() - 1, 1);
        f = ymdLocal(prevMonth);
        t = lastOfMonth(prevMonth); break; }
      case 'ytd':
        f = ymdLocal(new Date(today.getFullYear(), 0, 1));
        t = ymdLocal(today); break;
      case 'all':
      default:
        f = ''; t = ''; break;
    }
    setDatePreset(val);
    setFrom(f);
    setTo(t);
  };
  useEffect(() => {
    let params;
    try {
      params = new URLSearchParams(location.search || '');
    } catch {
      return;
    }

    if ((params.get('agent_view') || '').trim().toLowerCase() !== 'invoices') return;

    const validYmd = (value) => /^\d{4}-\d{2}-\d{2}$/.test(String(value || '').trim());
    const nextFrom = validYmd(params.get('agent_from')) ? String(params.get('agent_from')).trim() : '';
    const nextTo = validYmd(params.get('agent_to')) ? String(params.get('agent_to')).trim() : '';
    const nextQ = String(params.get('agent_q') || '').trim();
    const nextBalance = String(params.get('agent_balance') || '').trim().toLowerCase() === 'open' ? 'open' : 'all';
    const nextClientName = String(params.get('agent_clientname') || '').trim();

    setFrom(nextFrom);
    setTo(nextTo);
    setQ(nextQ);
    setDatePreset('');
    setStatusIds([]);
    setTypeIds([]);
    setBalanceFilter(nextBalance);
    setClientNameFilter(nextClientName);
    setSelected(new Set());
    setActiveId(null);

    [
      'agent_view',
      'agent_from',
      'agent_to',
      'agent_q',
      'agent_balance',
      'agent_clientname',
    ].forEach((key) => params.delete(key));

    const nextSearch = params.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : '',
      },
      { replace: true }
    );
  }, [location.pathname, location.search, navigate]);
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/invoices/invoicetypes`);
        const json = await res.json();
        setTypes((json || []).map(t => ({ id: Number(t.id), name: t.name })));
      } catch (e) { console.error('Failed to load invoice types', e); setTypes([]); }
    })();
  }, []);

  // --- Topbar title (set after render; clear on unmount) ---
  useEffect(() => {
    const subtitle = meta?.total > 0 ? `(${Number(meta.total).toLocaleString()} found)` : '';
    window.dispatchEvent(new CustomEvent('topbar:setTitle', {
      detail: { title: 'Invoices', subtitle }
    }));
    return () => {
      window.dispatchEvent(new CustomEvent('topbar:clearTitle'));
    };
  }, [meta?.total]);

  const applyStatusParams = (params) => {
    if (statusIds?.length > 0) {
      params.set('statusIds', statusIds.join(','));
      if (statusIds.length === 1) params.set('statusId', statusIds[0]);
    }
  };

  const applyTypeParams = (params) => {
    if (typeIds?.length > 0) {
      params.set('typeIds', typeIds.join(','));
      if (typeIds.length === 1) params.set('typeId', typeIds[0]);
    }
  };

  const fetchInvoices = async (offset = meta.offset, limit = meta.limit, opts = {}) => {
    const { recenter = true } = opts || {};
    setLoading(true);
    try {
      const params = new URLSearchParams({ sortField, sortOrder, limit, offset });
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      if (q) params.set('q', q);
      if (!IS_CLIENT) {
        const cid = resolvedInternalClientId;
        if (cid) params.set('clientid', cid);
        else if (clientNameFilter) params.set('clientname', clientNameFilter);
      }
      applyStatusParams(params);
      applyTypeParams(params);
      if (balanceFilter === 'open') params.set('balance', 'open');

      if (IS_CLIENT) {
        const enforced = selectedClientId || '';
        if (enforced) params.set('clientid', enforced);
        params.set('hideCanceled', '1');
      }

      const res = await fetch(`${API}/api/invoices/overview?${params.toString()}`);
      const json = await res.json();

      const total = Number(json.meta?.total || 0);
      const safeLimit = Number.isFinite(+limit) && +limit > 0 ? +limit : DEFAULT_LIMIT;
      const requestedPage = Math.floor((offset || 0) / safeLimit) + 1;
      const computedLastPage = Math.max(1, Math.ceil(total / safeLimit));

      // If the requested page is out of range (e.g., after filters), recenter once
      if (recenter && requestedPage > computedLastPage) {
        const newOffset = Math.max(0, (computedLastPage - 1) * safeLimit);
        setMeta(prev => ({ ...prev, total, totals: json.meta?.totals ?? prev.totals, limit: safeLimit, offset: newOffset }));
        await fetchInvoices(newOffset, safeLimit, { recenter: false });
        return;
      }

      setData(json.data || []);
      setMeta(prev => ({ total, totals: json.meta?.totals ?? prev.totals, limit: safeLimit, offset }));
      setSelected(new Set());
    } catch (err) {
      console.error('Failed to fetch invoices overview:', err);
    } finally {
      setLoading(false);
    }
  };

  // Debounced auto-refresh when filters/sorting change (including client name).
  // This keeps typing responsive by avoiding a fetch on every keystroke.
  const invoicesFetchDebounceRef = useRef(null);
  const invoicesLastQueryRef = useRef('');
  const invoicesFirstLoadRef = useRef(true);
  useEffect(() => {
    const rawClient = (clientNameFilter || '').trim();
    // Only auto-search by client name when there is a meaningful term;
    // very short terms (e.g., 1–2 chars) rely on Enter/Refresh instead.
    const effectiveClientId = !IS_CLIENT ? (resolvedInternalClientId || '') : '';
    const effectiveClientName = !effectiveClientId && rawClient.length >= 3 ? rawClient : '';

    const signature = JSON.stringify({
      sortField,
      sortOrder,
      statusIds: (statusIds || []).slice().sort(),
      typeIds: (typeIds || []).slice().sort(),
      from: from || '',
      to: to || '',
      q: (q || '').trim(),
      balanceFilter,
      clientId: effectiveClientId,
      clientNameFilter: effectiveClientName,
      IS_CLIENT: !!IS_CLIENT,
      CLIENT_ID: CLIENT_ID || '',
      selectedClientId: selectedClientId || '',
    });

    if (!invoicesFirstLoadRef.current && invoicesLastQueryRef.current === signature) return;
    invoicesLastQueryRef.current = signature;

    if (invoicesFetchDebounceRef.current) {
      clearTimeout(invoicesFetchDebounceRef.current);
      invoicesFetchDebounceRef.current = null;
    }

    if (invoicesFirstLoadRef.current) {
      invoicesFirstLoadRef.current = false;
      fetchInvoices(meta.offset, meta.limit);
      return;
    }
    // Heavier debounce so typing (especially in client filter) stays snappy:
    // only hit the server after ~1.2s of no changes.
    invoicesFetchDebounceRef.current = setTimeout(() => {
      fetchInvoices(meta.offset, meta.limit);
    }, 1200);

    return () => {
      if (invoicesFetchDebounceRef.current) {
        clearTimeout(invoicesFetchDebounceRef.current);
        invoicesFetchDebounceRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortField, sortOrder, statusIds, typeIds, from, to, q, balanceFilter, clientNameFilter, resolvedInternalClientId, IS_CLIENT, CLIENT_ID, selectedClientId]);

  const handleSort = (field) => { if (sortField === field) setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC'); else { setSortField(field); setSortOrder('ASC'); } };

  const toggleSelect = (invoiceId) => { const next = new Set(selected); if (next.has(invoiceId)) next.delete(invoiceId); else next.add(invoiceId); setSelected(next); };
  const toggleSelectAllOnPage = () => { if (selected.size === data.length) setSelected(new Set()); else setSelected(new Set(data.map(d => d.invoiceId))); };
  const ensureRowActive = (invoiceId) => {
    setActiveId(prev => (prev === invoiceId ? prev : invoiceId));
  };

  const exportCsv = () => {
    const params = new URLSearchParams({ sortField, sortOrder });
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    if (q) params.set('q', q);
    applyStatusParams(params);
    if (balanceFilter === 'open') params.set('balance', 'open');
    if (IS_CLIENT) {
      const enforced = selectedClientId || '';
      if (enforced) params.set('clientid', enforced);
      params.set('hideCanceled', '1');
    }
    window.open(`${API}/api/invoices/overview/export?${params.toString()}`, '_blank');
  };

  const batchRecompute = async () => {
    if (!selected.size) { alert('Select at least one invoice.'); return; }
    if (!window.confirm('Recompute selected invoices?')) return;
    try {
      await Promise.all(Array.from(selected).map((id) =>
        fetch(`${API}/api/invoices/${id}/recompute`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userFullName: getUserFullName() }),
        }).then(r => r.json())
      ));
      alert('Recompute complete');
      fetchInvoices(meta.offset, meta.limit);
    } catch (e) { console.error(e); alert('Batch recompute failed.'); }
  };

  const changeStatus = async (id, newStatus) => {
    try {
      await fetch(`${API}/api/invoices/${id}/status`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus, userFullName: getUserFullName() }),
      });
      fetchInvoices(meta.offset, meta.limit);
    } catch (e) { console.error(e); alert('Failed to update status.'); }
  };

  const bulkMarkBadDebtSelected = async () => {
    if (!selected.size) { alert('Select at least one invoice.'); return; }
    if (!window.confirm(`Mark ${selected.size} selected invoice(s) as bad debt? You can reopen them later if needed.`)) return;
    try {
      const ids = Array.from(selected);
      for (const id of ids) {
        try {
          await fetch(`${API}/api/invoices/${id}/status`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ status: 6, userFullName: getUserFullName() }),
          });
        } catch (e) {
          console.error('Mark bad debt failed for', id, e);
        }
        await new Promise(r => setTimeout(r, 100)); // tiny delay to avoid thrash
      }
      alert('Mark bad debt complete.');
      setSelected(new Set());
      fetchInvoices(meta.offset, meta.limit);
    } catch (e) {
      console.error(e);
      alert('Bulk mark bad debt failed.');
    }
  };

  const cancelInvoice = async (id) => {
    if (!window.confirm(`Cancel invoice #${id}? This will mark it as canceled but keep it for history.`)) return;
    await changeStatus(id, 5);
    setSelected(prev => { const next = new Set(prev); next.delete(id); return next; });
  };

  const duplicateInvoice = async (id) => {
    if (!id) return;
    if (!window.confirm(`Duplicate invoice #${id} using today's date?`)) return;
    const toastId = `dup-invoice-${id}`;
    try { showToast({ id: toastId, message: `Duplicating invoice #${id}…`, duration: 0, align: 'center', slideFrom: 'top', pill: true }); } catch {}
    try {
      const res = await fetch(`${API}/api/invoices/${id}`);
      if (!res.ok) throw new Error('Failed to load invoice');
      const inv = await res.json();
      const header = inv?.header || {};
      const items = Array.isArray(inv?.items) ? inv.items : [];
      const payload = {
        clientid: header.clientid,
        date: ymdLocal(new Date()),
        notes: header.notes || null,
        type: header.type,
        userFullName: getUserFullName() || 'system',
        items: items.map(it => ({
          qty: it.qty,
          amt: it.amt,
          chargeitemid: it.chargeitemid,
          chargename: it.chargename,
          description: it.description,
          chargetype: it.chargetype,
          reference: it.reference,
          warehouseid: it.warehouseid,
        })),
        receiptIds: inv?.receiptIds || [],
        orderIds: inv?.orderIds || [],
        eorderIds: inv?.eorderIds || [],
      };
      const createRes = await fetch(`${API}/api/invoices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!createRes.ok) {
        const err = await createRes.json().catch(() => ({}));
        throw new Error(err?.error || 'Failed to duplicate invoice');
      }
      const created = await createRes.json().catch(() => ({}));
      try { updateToast({ id: toastId, message: `Duplicated invoice #${id}${created?.id ? ` → #${created.id}` : ''}`, duration: 2500, align: 'center', slideFrom: 'top', pill: true }); } catch {}
      fetchInvoices(0, meta.limit, { recenter: false });
    } catch (e) {
      console.error(e);
      try { updateToast({ id: toastId, message: 'Failed to duplicate invoice.', duration: 2500, align: 'center', slideFrom: 'top', pill: true }); } catch {}
      alert(e?.message || 'Failed to duplicate invoice.');
    }
  };

  const bulkCancelSelected = async () => {
    if (!selected.size) { alert('Select at least one invoice.'); return; }
    if (!window.confirm(`Cancel ${selected.size} selected invoice(s)? This will mark them as canceled but keep them for history.`)) return;
    try {
      const ids = Array.from(selected);
      for (const id of ids) {
        try {
          await fetch(`${API}/api/invoices/${id}/status`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ status: 5, userFullName: getUserFullName() }),
          });
        } catch (e) {
          console.error('Cancel failed for', id, e);
        }
        await new Promise(r => setTimeout(r, 100)); // tiny delay to avoid thrash
      }
      alert('Cancel complete.');
      setSelected(new Set());
      fetchInvoices(meta.offset, meta.limit);
    } catch (e) {
      console.error(e);
      alert('Bulk cancel failed.');
    }
  };

  const page = Math.floor((meta.offset || 0) / (meta.limit || DEFAULT_LIMIT)) + 1;
  const lastPage = Math.max(1, Math.ceil((meta.total || 0) / (meta.limit || DEFAULT_LIMIT)));

  const gotoPage = (p) => {
    const limit = meta.limit || DEFAULT_LIMIT;
    const lp = Math.max(1, Math.ceil((meta.total || 0) / (limit || DEFAULT_LIMIT)));
    const clamped = Math.min(Math.max(1, p), lp);
    const nextOffset = Math.max(0, (clamped - 1) * limit);
    fetchInvoices(nextOffset, limit);
  };

  const [colsOpen, setColsOpen] = useState(false);
  const [presets, setPresets] = useState(loadPresets());
  const [activePreset, setActivePreset] = useState('');
  const searchRef = useRef(null);
  const tableWrapRef = useRef(null);
  const invHeaderRowRef = useRef(null);
  const invTableRef = useRef(null);
  const invResizeState = useRef({ key: null, startX: 0, startW: 0 });
  const [invColWidths, setInvColWidths] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_INV_COLWIDTHS_KEY);
      const j = raw ? JSON.parse(raw) : {};
      return (j && typeof j === 'object') ? j : {};
    } catch { return {}; }
  });
  useEffect(() => { try { localStorage.setItem(LS_INV_COLWIDTHS_KEY, JSON.stringify(invColWidths)); } catch {} }, [invColWidths]);

  const ensureInvWidthsLocked = () => {
    const row = invHeaderRowRef.current; if (!row) return;
    const ths = Array.from(row.children || []);
    if (!ths.length) return;
    // if already have all col widths accounted for, skip
    const renderedKeys = ['select', 'actions', ...COLUMN_DEFS.filter(c => visibleCols.has(c.key)).map(c => c.key)];
    const haveAll = renderedKeys.every(k => invColWidths[k] != null);
    if (haveAll) return;
    const next = { ...invColWidths };
    renderedKeys.forEach((key, idx) => {
      const th = ths[idx];
      if (th && next[key] == null) next[key] = th.offsetWidth;
    });
    setInvColWidths(next);
  };
  const onInvResizeMove = (e) => {
    const { key, startX, startW } = invResizeState.current; if (!key) return;
    const delta = e.clientX - startX; const nextW = Math.max(MIN_COL_W, startW + delta);
    setInvColWidths(prev => ({ ...prev, [key]: nextW })); e.preventDefault();
  };
  const onInvResizeUp = () => {
    invResizeState.current = { key: null, startX: 0, startW: 0 };
    try { document.body.classList.remove('col-resizing'); } catch {}
    window.removeEventListener('mousemove', onInvResizeMove);
    window.removeEventListener('mouseup', onInvResizeUp);
  };
  const startInvResize = (key, e) => {
    ensureInvWidthsLocked();
    const th = e.currentTarget.parentElement;
    invResizeState.current = { key, startX: e.clientX, startW: th.offsetWidth };
    try { document.body.classList.add('col-resizing'); } catch {}
    window.addEventListener('mousemove', onInvResizeMove);
    window.addEventListener('mouseup', onInvResizeUp);
    e.preventDefault(); e.stopPropagation();
  };

  const autoFitInvCol = (key) => {
    try {
      const keys = ['select','actions', ...COLUMN_DEFS.filter(c => visibleCols.has(c.key)).map(c => c.key)];
      const idx = keys.findIndex(k => k === key);
      if (idx === -1) return;
      let maxW = 0;
      if (invHeaderRowRef.current?.children[idx]) {
        maxW = Math.max(maxW, Math.ceil(invHeaderRowRef.current.children[idx].scrollWidth));
      }
      const tbl = invTableRef.current;
      if (tbl?.tBodies?.[0]) {
        const rows = tbl.tBodies[0].rows;
        for (let r = 0; r < rows.length; r++) {
          const cell = rows[r].children[idx];
          if (cell) maxW = Math.max(maxW, Math.ceil(cell.scrollWidth));
        }
      }
      const w = Math.max(MIN_COL_W, Math.min(800, maxW));
      setInvColWidths(prev => ({ ...prev, [key]: w }));
    } catch {}
  };
  const colsBtnRef = useRef(null);
  const colsPopoverRef = useRef(null);

  const handleLimitUpdate = (raw) => {
    let v = parseInt(raw ?? limitInput, 10);
    if (isNaN(v)) v = meta.limit || DEFAULT_LIMIT;
    if (v < 1) v = 1; if (v > 10000) v = 10000;
    setMeta(m => ({ ...m, limit: v, offset: 0 }));
    setLimitInput(String(v));
    savePersisted({ from, to, q, statusIds, typeIds, sortField, sortOrder, limit: v, offset: 0, datePreset, balanceFilter });
    fetchInvoices(0, v);
  };

  const renderSortHeader = (key, label, field) => (
    <th
      data-colkey={key}
      onClick={(e) => { if (e?.target && typeof e.target.closest === 'function' && e.target.closest('.col-resizer')) return; handleSort(field); }}
      className={sortField === field ? 'active-sort' : ''}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        {label} {sortField === field ? (sortOrder === 'ASC' ? '▲' : '▼') : ''}
      </span>
      <span
        className="col-resizer"
        onMouseDown={(e)=>startInvResize(key, e)}
        onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }}
        onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitInvCol(key); }}
      />
    </th>
  );

  const { totalAmount = 0, amountPaid = 0, amountDue = 0 } = meta?.totals ?? {};

  const resetFilters = () => {
    setFrom(''); setTo(''); setQ(''); setStatusIds([]); setTypeIds([]); setBalanceFilter('all');
    setSortField('invoiceDate'); setSortOrder('DESC');
    setMeta(m => ({ ...m, offset: 0, limit: DEFAULT_LIMIT }));
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
    fetchInvoices(0, DEFAULT_LIMIT);
  };

  // Ensure newly added columns (like 'docRef', 'createdBy', 'notes') appear and reposition 'status' after 'invoiceId'
  const initialColsRaw = loadColumns() || DEFAULT_VISIBLE;
  const initialCols = (() => {
    try {
      const arr = Array.isArray(initialColsRaw) ? [...initialColsRaw] : [...DEFAULT_VISIBLE];
      // ensure docRef exists (after invoiceType)
      if (!arr.includes('docRef')) {
        const at = arr.indexOf('invoiceType');
        if (at !== -1) arr.splice(at + 1, 0, 'docRef'); else arr.push('docRef');
      }
      // ensure createdBy exists (just before notes when present)
      if (!arr.includes('createdBy')) {
        const atNotes = arr.indexOf('notes');
        if (atNotes !== -1) arr.splice(atNotes, 0, 'createdBy'); else arr.push('createdBy');
      }
      // ensure notes exists (at end)
      if (!arr.includes('notes')) arr.push('notes');
      // reposition status right after invoiceId
      const atInv = arr.indexOf('invoiceId');
      const atStatus = arr.indexOf('status');
      if (atInv !== -1 && atStatus !== -1 && atStatus !== atInv + 1) {
        arr.splice(atStatus, 1);
        arr.splice(atInv + 1, 0, 'status');
      }
      saveColumns(arr);
      return arr;
    } catch { return [...DEFAULT_VISIBLE]; }
  })();
  const [visibleCols, setVisibleCols] = useState(new Set(initialCols));
  useEffect(() => { saveColumns(Array.from(visibleCols)); }, [visibleCols]);
  useEffect(() => {
    const onDoc = (e) => {
      if (!colsOpen) return;
      const t = e.target;
      if (colsPopoverRef.current && !colsPopoverRef.current.contains(t) && colsBtnRef.current && !colsBtnRef.current.contains(t) ) {
        setColsOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [colsOpen]);
  const toggleCol = (key) => setVisibleCols(prev => { const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  const allCols = () => setVisibleCols(new Set(DEFAULT_VISIBLE));
  const noneCols = () => setVisibleCols(new Set());

  const statusSlugOf = (row) => {
    const label = (row.statusName || row.status || '').toString().trim().toLowerCase();
    return label ? `status-${label.replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}` : '';
  };

  const showNewButton = !IS_CLIENT;

  // ===== RECEIVE PAYMENT (client name display + payment types dropdown) =====
  const [paymentTypes, setPaymentTypes] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/paymenttypes`);
        const json = await res.json();
        setPaymentTypes(Array.isArray(json) ? json : []);
      } catch {
        setPaymentTypes([]);
      }
    })();
  }, []);

  const [payOpen, setPayOpen] = useState(false);
  const [payForm, setPayForm] = useState({
    clientid: '',
    clientName: '',
    date: new Date().toISOString().slice(0, 10),
    paymenttype: '',
    amt: '',
    reference: '',
    notes: '',
    allocations: [], // {invoiceid, amountDue, apply, clientName, invoiceDate}
  });
  const [creditRows, setCreditRows] = useState([]);
  const [creditLoading, setCreditLoading] = useState(false);
  const [creditError, setCreditError] = useState('');
  const [selectedCreditIds, setSelectedCreditIds] = useState(() => new Set());
  const creditTypeId = useMemo(() => {
    const hit = paymentTypes.find(pt => String(pt.name || '').trim().toLowerCase() === 'credit');
    return hit?.id ?? null;
  }, [paymentTypes]);
  const isCreditPayment = creditTypeId != null && String(payForm.paymenttype) === String(creditTypeId);

  // Hover popover for Doc # (coalesced refs)
  const [docHoverId, setDocHoverId] = useState(null);
  const docHoverTimerRef = useRef(null);
  const startDocHover = (id) => { if (docHoverTimerRef.current) clearTimeout(docHoverTimerRef.current); setDocHoverId(id); };
  const endDocHover = () => { if (docHoverTimerRef.current) clearTimeout(docHoverTimerRef.current); docHoverTimerRef.current = setTimeout(() => setDocHoverId(null), 120); };
  useEffect(() => () => { if (docHoverTimerRef.current) clearTimeout(docHoverTimerRef.current); }, []);

  const docPopoverStyles = {
    container: { position: 'relative', display: 'inline-flex', alignItems: 'center' },
    bubble: {
      position: 'absolute',
      top: '100%',
      left: 0,
      zIndex: 2000,
      width: 360,
      maxWidth: '90vw',
      maxHeight: 260,
      overflowY: 'auto',
      marginTop: 6,
      background: '#ffffff',
      color: '#000000',
      border: '1px solid #cccccc',
      borderRadius: 8,
      boxShadow: '0 8px 24px rgba(0,0,0,.25)',
      padding: '8px 10px',
    },
    faded: { opacity: 0.5 },
    list: { display: 'flex', flexDirection: 'column', gap: 6, padding: 0, margin: 0 },
    listItem: {
      display: 'block',
      padding: '6px 8px',
      border: '1px solid #cccccc',
      borderRadius: 6,
      background: '#f6f6f6',
      color: '#111',
      fontSize: 12,
      lineHeight: 1.3,
    },
  };

  const fetchInvoiceHeader = async (invoiceId) => {
    const res = await fetch(`${API}/api/invoices/${invoiceId}`);
    if (!res.ok) throw new Error('Failed to fetch invoice header');
    const j = await res.json();
    return {
      clientid: String(j?.header?.clientid || ''),
      clientName: j?.header?.clientName || '',
      email: j?.header?.email || '',
      billingemail: j?.header?.clientBillingEmail || j?.header?.billingemail || ''
    };
  };

  const openReceivePayment = async () => {
    if (!selected.size) { alert('Select at least one invoice.'); return; }
    const selectedRows = data.filter(r => selected.has(r.invoiceId));
    if (!selectedRows.length) { alert('Select at least one invoice.'); return; }

    // Determine client from the first selected invoice via header (reliable clientid)
    let clientMeta;
    try {
      clientMeta = await fetchInvoiceHeader(selectedRows[0].invoiceId);
    } catch (e) {
      alert('Could not load invoice details to determine client.');
      return;
    }

    // Ensure all selected invoices belong to the same client (by client name comparison first)
    const mismatch = selectedRows.some(r => (r.clientName || '').trim() !== (clientMeta.clientName || '').trim());
    if (mismatch) {
      alert('Please select invoices from the same client to receive a single payment.');
      return;
    }

    const allocations = selectedRows.map(r => ({
      invoiceid: r.invoiceId,
      amountDue: Number(r.amountDue || 0),
      apply: Number(r.amountDue || 0),
      clientName: r.clientName,
      invoiceDate: r.invoiceDate
    }));
    const totalApply = allocations.reduce((s,a)=>s + (a.apply||0), 0);

    setPayForm({
      clientid: clientMeta.clientid,
      clientName: clientMeta.clientName,
      date: new Date().toISOString().slice(0, 10),
      paymenttype: '',
      amt: totalApply.toFixed(2),
      reference: '',
      notes: '',
      allocations
    });
    setSelectedCreditIds(new Set());
    setCreditRows([]);
    setCreditError('');
    setPayOpen(true);
  };

  const openReceivePaymentSingle = async (row) => {
    try {
      const clientMeta = await fetchInvoiceHeader(row.invoiceId);
      const allocation = {
        invoiceid: row.invoiceId,
        amountDue: Number(row.amountDue || 0),
        apply: Number(row.amountDue || 0),
        clientName: row.clientName,
        invoiceDate: row.invoiceDate
      };
      setPayForm({
        clientid: clientMeta.clientid,
        clientName: clientMeta.clientName,
        date: new Date().toISOString().slice(0, 10),
        paymenttype: '',
        amt: allocation.apply.toFixed(2),
        reference: '',
        notes: '',
        allocations: [allocation]
      });
      setSelectedCreditIds(new Set());
      setCreditRows([]);
      setCreditError('');
      setPayOpen(true);
    } catch (e) {
      alert('Could not open Receive Payment. Failed to fetch invoice details.');
    }
  };

  const totalApplied = useMemo(() => {
    return payForm.allocations.reduce((s,a)=> s + (Number(a.apply)||0), 0);
  }, [payForm.allocations]);

  const totalCreditAvailable = useMemo(() => {
    return creditRows.reduce((s, r) => s + (Number(r.available) || 0), 0);
  }, [creditRows]);
  const selectedCreditTotal = useMemo(() => {
    return creditRows.reduce((s, r) => selectedCreditIds.has(r.paymentid) ? s + (Number(r.available) || 0) : s, 0);
  }, [creditRows, selectedCreditIds]);
  const creditInsufficient = isCreditPayment && totalApplied > selectedCreditTotal + 0.01;
  const creditSelectionEmpty = isCreditPayment && selectedCreditIds.size === 0;
  const allocationBaseSig = useMemo(() => {
    return payForm.allocations
      .map(a => `${a.invoiceid}:${Number(a.amountDue || 0)}`)
      .join('|');
  }, [payForm.allocations]);

  const computeCreditAllocations = useCallback((allocs, maxApply) => {
    let remaining = Math.max(0, Math.round(Number(maxApply || 0) * 100));
    return allocs.map(a => {
      const dueCents = Math.max(0, Math.round(Number(a.amountDue || 0) * 100));
      const useCents = Math.min(dueCents, remaining);
      remaining -= useCents;
      return { ...a, apply: Number((useCents / 100).toFixed(2)) };
    });
  }, []);

  useEffect(() => {
    if (!isCreditPayment) return;
    setPayForm(f => {
      const next = computeCreditAllocations(f.allocations, selectedCreditTotal);
      const changed = next.some((n, i) => Number(n.apply || 0) !== Number(f.allocations[i]?.apply || 0));
      return changed ? { ...f, allocations: next } : f;
    });
  }, [isCreditPayment, selectedCreditTotal, allocationBaseSig, computeCreditAllocations]);

  useEffect(() => {
    if (!payOpen) {
      setCreditRows([]);
      setSelectedCreditIds(new Set());
      setCreditError('');
      setCreditLoading(false);
      return;
    }
    if (!isCreditPayment || !payForm.clientid) {
      setCreditRows([]);
      setSelectedCreditIds(new Set());
      setCreditError('');
      return;
    }
    (async () => {
      setCreditLoading(true);
      setCreditError('');
      try {
        const res = await fetch(`${API}/api/payments/credits?clientid=${encodeURIComponent(payForm.clientid)}`);
        const j = await res.json();
        if (!res.ok) throw new Error(j?.error || 'Failed to load credits');
        setCreditRows(Array.isArray(j) ? j : []);
      } catch (e) {
        setCreditRows([]);
        setCreditError(e?.message || 'Failed to load credits');
      } finally {
        setCreditLoading(false);
      }
    })();
  }, [payOpen, isCreditPayment, payForm.clientid]);

  useEffect(() => {
    if (!isCreditPayment) {
      setSelectedCreditIds(new Set());
      setCreditError('');
    }
  }, [isCreditPayment]);

  const submitReceivePayment = async (e) => {
    e?.preventDefault?.();
    try {
      if (!payForm.clientid) { alert('Client is required'); return; }
      if (!payForm.date || !payForm.paymenttype) { alert('Date and Payment Type are required'); return; }

      const allocs = payForm.allocations
        .map(a => ({ invoiceid: Number(a.invoiceid), amt: Number(a.apply || 0) }))
        .filter(a => a.invoiceid && a.amt > 0);

      if (!allocs.length) { alert('No allocations entered.'); return; }

      if (isCreditPayment) {
        if (!selectedCreditIds.size) { alert('Select at least one credit to apply.'); return; }
        if (totalApplied <= 0) { alert('Total applied must be greater than zero.'); return; }
        if (selectedCreditTotal <= 0) { alert('Selected credits have no available balance.'); return; }

        const maxApply = selectedCreditTotal;
        const cappedAllocs = [];
        let remaining = maxApply;
        for (const a of allocs) {
          if (remaining <= 0) break;
          const use = Math.min(Number(a.amt || 0), remaining);
          if (use > 0) {
            cappedAllocs.push({ invoiceid: a.invoiceid, amt: Number(use.toFixed(2)) });
            remaining -= use;
          }
        }
        const cappedSum = cappedAllocs.reduce((s, a) => s + Number(a.amt || 0), 0);
        if (cappedSum <= 0) { alert('Selected credits do not cover any allocation.'); return; }

        const body = {
          clientid: payForm.clientid,
          credits: Array.from(selectedCreditIds),
          allocations: cappedAllocs,
          userFullName: getUserFullName() || 'system',
        };
        const res = await fetch(`${API}/api/payments/apply-credits`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        });
        const j = await res.json();
        if (!res.ok) throw new Error(j?.error || 'Failed to apply credits');
        const partial = cappedSum + 0.01 < totalApplied;
        alert(`Credits applied to ${j.invoices || cappedAllocs.length} invoice(s).${partial ? ' (Partial credit applied)' : ''}`);
      } else {
        const amt = Number(payForm.amt);
        if (!Number.isFinite(amt) || amt <= 0) { alert('Total amount is required'); return; }

        const body = {
          clientid: payForm.clientid,
          date: payForm.date,
          paymenttype: payForm.paymenttype, // id value from dropdown
          amt: amt,
          reference: payForm.reference || '',
          notes: payForm.notes || '',
          userFullName: getUserFullName() || 'system',
          allocations: allocs
        };

        const res = await fetch(`${API}/api/payments/apply`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        });
        const j = await res.json();
        if (!res.ok) throw new Error(j?.error || 'Failed to save payment');

        alert(`Payment #${j.id} recorded and applied.`);
      }
      setPayOpen(false);
      setSelected(new Set());
      fetchInvoices(meta.offset, meta.limit);
    } catch (err) {
      console.error(err);
      alert(err.message || 'Failed to record payment');
    }
  };

  // ===== EMAIL INVOICE =====
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailStorageChoice, setEmailStorageChoice] = useState('detail'); // 'summary' | 'detail'
  const [emailForm, setEmailForm] = useState({
    invoiceId: null,
    to: '',
    cc: '',
    bcc: '',
    subject: '',
    message: '',
  });
  const [emailModalAttachments, setEmailModalAttachments] = useState([]); // [{filename,date,description}]
  const [emailAttachmentsEnabled, setEmailAttachmentsEnabled] = useState(false);
  const [emailAttachmentsLoading, setEmailAttachmentsLoading] = useState(false);
  const [emailAttachmentsLoaded, setEmailAttachmentsLoaded] = useState(false);

  // ===== EMAIL MULTIPLE INVOICES =====
  const [emailBatchOpen, setEmailBatchOpen] = useState(false);
  const [emailBatchSending, setEmailBatchSending] = useState(false);
  const [emailBatchSummarizeStorage, setEmailBatchSummarizeStorage] = useState(false);
  const [usePerInvoiceEmail, setUsePerInvoiceEmail] = useState(false);
  const [overrideEmailTo, setOverrideEmailTo] = useState(false);
  const [sendAsMergedPdf, setSendAsMergedPdf] = useState(false);
  const [emailBatchForm, setEmailBatchForm] = useState({
    ids: [], // array of invoiceIds
    to: '',
    cc: '',
    bcc: '',
    subject: 'Invoice #{invoiceId}',
    message: 'Dear {clientName},\n\nPlease find attached Invoice #{invoiceId}.\n\nThank you.'
  });

  // cache of per-invoice header data gathered at open-time
  const [emailBatchHeaders, setEmailBatchHeaders] = useState([]);

  function formatTemplate(tpl, meta) {
    if (!tpl) return '';
    return String(tpl)
      .replaceAll('{invoiceId}', String(meta.invoiceId ?? ''))
      .replaceAll('{clientName}', String(meta.clientName ?? ''));
  }

  function parseEmailList(value) {
    return String(value || '')
      .split(/[;,]/)
      .map(s => s.trim())
      .filter(Boolean);
  }

  function normalizeEmailListKey(value) {
    return parseEmailList(value).join(';').toLowerCase();
  }

  const openEmailInvoices = async () => {
    if (!selected.size) { alert('Select at least one invoice.'); return; }
    const ids = Array.from(selected);

    try {
      const headers = await Promise.all(ids.map(async (id) => {
        try {
          const h = await fetchInvoiceHeader(id);
          return {
            invoiceId: id,
            clientName: h.clientName || '',
            email: String(h.billingemail || h.email || '').trim(),
            emailKey: normalizeEmailListKey(h.billingemail || h.email || '')
          };
        } catch {
          return { invoiceId: id, clientName: '', email: '', emailKey: '' };
        }
      }));

      const distinctEmailKeys = Array.from(new Set(headers.map(h => h.emailKey).filter(Boolean)));
      const sameEmail = distinctEmailKeys.length === 1
        ? (headers.find(h => h.emailKey === distinctEmailKeys[0])?.email || '')
        : '';

      const defaultUsePer = distinctEmailKeys.length > 1; // many emails -> per-invoice
      setUsePerInvoiceEmail(defaultUsePer);
      setOverrideEmailTo(!defaultUsePer); // single email -> override
      setSendAsMergedPdf(false);

      setEmailBatchForm({
        ids,
        to: sameEmail,
        cc: '',
        bcc: '',
        subject: 'Invoice #{invoiceId}',
        message: 'Dear {clientName},\n\nPlease find attached Invoice #{invoiceId}.\n\nThank you.'
      });
      setEmailBatchHeaders(headers);
      setEmailBatchOpen(true);
    } catch (e) {
      console.error(e);
      alert('Failed to prepare batch email.');
    }
  };

  // Helper: ArrayBuffer to base64
  function abToBase64(buf){
    const bytes = new Uint8Array(buf);
    const chunk = 0x8000;
    let binary = '';
    for (let i=0; i<bytes.length; i+=chunk){
      binary += String.fromCharCode.apply(null, bytes.subarray(i, i+chunk));
    }
    return btoa(binary);
  }
  function guessContentType(name='') {
    const n = String(name).toLowerCase();
    if (n.endsWith('.pdf')) return 'application/pdf';
    if (n.endsWith('.png')) return 'image/png';
    if (n.endsWith('.jpg') || n.endsWith('.jpeg')) return 'image/jpeg';
    if (n.endsWith('.gif')) return 'image/gif';
    if (n.endsWith('.csv')) return 'text/csv';
    if (n.endsWith('.txt')) return 'text/plain';
    return 'application/octet-stream';
  }

  const sendBatchEmails = async (e) => {
    e?.preventDefault?.();
    if (!emailBatchForm.ids.length) { setEmailBatchOpen(false); return; }

    // Validation for merged PDF mode
    if (sendAsMergedPdf) {
      if (!overrideEmailTo && !emailBatchForm.to.trim()) {
        alert('Please enter a recipient email for the merged PDF.');
        return;
      }
    }

    if (overrideEmailTo && !emailBatchForm.to.trim()) {
      alert('Please enter the override recipient email.');
      return;
    }
    if (!overrideEmailTo && !usePerInvoiceEmail && !emailBatchForm.to.trim()) {
      alert('Please enter a recipient email (or enable per-invoice emails).');
      return;
    }

    setEmailBatchSending(true);

    // Build the list of email targets for non-merged mode
    async function buildTargets() {
      const targets = [];
      for (const id of emailBatchForm.ids) {
        let meta = emailBatchHeaders.find(h => h.invoiceId === id);
        if (!meta) {
          // Fallback: fetch header if not cached
          try {
            const h = await fetchInvoiceHeader(id);
            meta = { invoiceId: id, clientName: h.clientName || '', email: (h.billingemail || h.email || '').trim() };
          } catch {
            meta = { invoiceId: id, clientName: '', email: '' };
          }
        }
        const toAddrRaw = overrideEmailTo
          ? (emailBatchForm.to || '')
          : (usePerInvoiceEmail ? (meta.email || '') : (emailBatchForm.to || ''));
        const toAddr = String(toAddrRaw || '').trim();
        targets.push({ id, meta, toAddr });
      }
      return targets;
    }

    // Single email with ONE merged PDF attachment
    if (sendAsMergedPdf) {
      try {
        let PDFDocument; ({ PDFDocument } = await import('pdf-lib'));
        const merged = await PDFDocument.create();
        // Fetch and append each invoice PDF sequentially
        for (const id of emailBatchForm.ids) {
          const res = await fetch(`${API}/api/invoices/${id}/pdf`);
          if (!res.ok) throw new Error(`Failed to fetch PDF for invoice ${id}`);
          const bytes = await res.arrayBuffer();
          const src = await PDFDocument.load(bytes);
          const pages = await merged.copyPages(src, src.getPageIndices());
          pages.forEach(p => merged.addPage(p));
        }
        const mergedBytes = await merged.save();
        const b64 = abToBase64(mergedBytes);

        const toAddr = String(emailBatchForm.to || '').trim();
        if (!toAddr) { alert('Please enter the recipient email (To).'); setEmailBatchSending(false); return; }

        // Send via existing invoice email endpoint, using the first selected invoice id.
        // Backend should prefer provided attachments when present.
        const firstId = emailBatchForm.ids[0];
        const payload = {
          to: toAddr,
          cc: emailBatchForm.cc.trim() || undefined,
          bcc: emailBatchForm.bcc.trim() || undefined,
          subject: emailBatchForm.subject.replace('{invoiceId}', `${emailBatchForm.ids[0]}-${emailBatchForm.ids[emailBatchForm.ids.length-1]}`) || `Invoices (${emailBatchForm.ids.length})`,
          message: emailBatchForm.message || 'Please find attached merged invoices.',
          userFullName: getUserFullName() || 'system',
          attachments: [
            { filename: `invoices_merged_${emailBatchForm.ids[0]}_to_${emailBatchForm.ids[emailBatchForm.ids.length-1]}.pdf`, contentBase64: b64, contentType: 'application/pdf' }
          ],
          useAttachmentOnly: true
        };

        const res = await fetch(`${API}/api/invoices/${firstId}/email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (!res.ok) throw new Error('Failed to send merged PDF email');

        setEmailBatchSending(false);
        setEmailBatchOpen(false);
        markInvoicesAsSent(firstId);
        try { showToast({ id: 'invoices.email.merged', message: `Email sent (merged PDF of ${emailBatchForm.ids.length} invoice${emailBatchForm.ids.length===1?'':'s'}).`, duration: 2500, pill: true }); } catch {}
        return;
      } catch (err) {
        console.error('Merged email failed', err);
        alert(err.message || 'Failed to send merged PDF email');
        setEmailBatchSending(false);
        return;
      }
    }

    let ok = 0, fail = 0;
    const sentIds = [];
    const targets = await buildTargets();

    // If using per-invoice emails, make sure at least one target has an address
    if (usePerInvoiceEmail && !overrideEmailTo) {
      const hasAny = targets.some(t => !!t.toAddr);
      if (!hasAny) {
        setEmailBatchSending(false);
        alert('None of the selected invoices have a billing email address. Enter an Override Email To or select different invoices.');
        return;
      }
    }

    for (const t of targets) {
      if (!t.toAddr) { fail++; continue; }
      const row = data.find(r => r.invoiceId === t.id);
      const isStorage = String(row?.invoiceTypeName || row?.type || row?.invoiceType || '').toLowerCase().includes('storage');
      const payload = {
        to: t.toAddr,
        cc: emailBatchForm.cc.trim() || undefined,
        bcc: emailBatchForm.bcc.trim() || undefined,
        subject: formatTemplate(emailBatchForm.subject, { invoiceId: t.id, clientName: t.meta.clientName }) || `Invoice #${t.id}`,
        message: formatTemplate(emailBatchForm.message, { invoiceId: t.id, clientName: t.meta.clientName }) || '',
        userFullName: getUserFullName() || 'system',
      };
      if (isStorage) payload.storageSummary = !!emailBatchSummarizeStorage;
      try {
        const res = await fetch(`${API}/api/invoices/${t.id}/email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error('send failed');
        ok++;
        sentIds.push(t.id);
      } catch (err) {
        console.error('Batch email failed for', t.id, err);
        fail++;
      }
      // small delay to avoid SMTP throttling / server saturation
      await new Promise(r => setTimeout(r, 150));
    }

    setEmailBatchSending(false);
    if (sentIds.length) markInvoicesAsSent(sentIds);
    setEmailBatchOpen(false);
    if (fail > 0) alert(`Some emails failed. Sent: ${ok}, Failed: ${fail}.`);
    else { try { showToast({ id: 'invoices.email.batch', message: `Sent ${ok} email${ok===1?'':'s'}.`, duration: 2500, pill: true }); } catch {} }
  };

  const fetchEmailAttachments = useCallback(async (invoiceId) => {
    if (!invoiceId) return;
    setEmailAttachmentsLoading(true);
    try {
      const coll = [];
      try {
        const res = await fetch(`${API}/api/invoices/${invoiceId}/attachments`);
        const list = res.ok ? await res.json() : [];
        for (const r of (Array.isArray(list) ? list : [])) {
          const fname = String(r.filename || '');
          coll.push({
            ...r,
            source: 'invoice',
            emailKey: `invoice:${invoiceId}:${fname}`,
            url: `${API}/api/invoices/${invoiceId}/attachments/${encodeURIComponent(fname)}`,
          });
        }
      } catch (err) {
        console.error('Failed to load invoice attachments', err);
      }

      const row = (data || []).find(r => Number(r.invoiceId) === Number(invoiceId)) || {};
      const recs = Array.isArray(row.receiptIds) ? row.receiptIds : (Number(row.receiptId) ? [Number(row.receiptId)] : []);
      const ords = Array.isArray(row.orderIds)   ? row.orderIds   : (Number(row.orderId)   ? [Number(row.orderId)]   : []);

      for (const oid of ords) {
        try {
          const r = await fetch(`${API}/api/orders/${oid}/attachments`);
          const list = r.ok ? await r.json() : [];
          for (const a of (Array.isArray(list) ? list : [])) {
            const fname = String(a.filename || '');
            coll.push({
              filename: fname,
              date: a.date || null,
              description: a.description || null,
              source: 'order',
              orderid: oid,
              emailKey: `order:${oid}:${fname}`,
              url: `${API}/api/orders/${oid}/attachments/${encodeURIComponent(fname)}`,
            });
          }
        } catch (err) {
          console.error(`Failed to load order ${oid} attachments`, err);
        }
      }

      for (const rid of recs) {
        try {
          const r = await fetch(`${API}/api/receipts/${rid}/attachments`);
          const list = r.ok ? await r.json() : [];
          for (const a of (Array.isArray(list) ? list : [])) {
            const fname = String(a.filename || '');
            coll.push({
              filename: fname,
              date: a.date || null,
              description: a.description || null,
              source: 'receipt',
              receiptid: rid,
              emailKey: `receipt:${rid}:${fname}`,
              url: `${API}/api/receipts/${rid}/attachments/${encodeURIComponent(fname)}`,
            });
          }
        } catch (err) {
          console.error(`Failed to load receipt ${rid} attachments`, err);
        }
      }

      coll.sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')) || String(a.filename||'').localeCompare(String(b.filename||'')));
      setEmailModalAttachments(coll);
      setEmailAttachmentsLoaded(true);
    } catch (err) {
      console.error('Failed to gather attachments', err);
      setEmailModalAttachments([]);
    } finally {
      setEmailAttachmentsLoading(false);
    }
  }, [API, data]);

  const handleEmailAttachmentsToggle = useCallback((checked) => {
    setEmailAttachmentsEnabled(checked);
    if (checked && emailForm.invoiceId && !emailAttachmentsLoaded && !emailAttachmentsLoading) {
      fetchEmailAttachments(emailForm.invoiceId);
    }
  }, [emailAttachmentsLoaded, emailAttachmentsLoading, fetchEmailAttachments, emailForm.invoiceId]);

  const openEmailInvoice = async (invoiceId) => {
    setEmailOpen(true);
    setEmailModalAttachments([]);
    setEmailAttachmentsEnabled(false);
    setEmailAttachmentsLoaded(false);
    setEmailAttachmentsLoading(false);
    setEmailForm({
      invoiceId,
      to: '',
      cc: '',
      bcc: '',
      subject: `Invoice #${invoiceId}`,
      message: `Preparing email for Invoice #${invoiceId}…`,
    });
    try {
      const meta = await fetchInvoiceHeader(invoiceId);
      const toDefault = String(meta.billingemail || meta.email || '').trim();
      setEmailForm({
        invoiceId,
        to: toDefault,
        cc: '',
        bcc: '',
        subject: `Invoice #${invoiceId}`,
        message: `Dear ${meta.clientName || 'customer'},\n\nPlease find attached Invoice #${invoiceId}.\n\nThank you.`,
      });
    } catch (e) {
      alert('Failed to load invoice header for email.');
    }
  };

  // Helper for base64
  function abToBase64(buf){
    const bytes = new Uint8Array(buf);
    const chunk = 0x8000;
    let binary = '';
    for (let i=0; i<bytes.length; i+=chunk){
      binary += String.fromCharCode.apply(null, bytes.subarray(i, i+chunk));
    }
    return btoa(binary);
  }

  // ===== Storage Print Prompt (modal) =====
  const [storagePrompt, setStoragePrompt] = useState({ open: false, invoiceId: null, title: '' });
  const openStoragePrompt = (invoiceId, title) => setStoragePrompt({ open: true, invoiceId, title: title || '' });
  const closeStoragePrompt = () => setStoragePrompt({ open: false, invoiceId: null, title: '' });
  const launchStoragePdf = (summary) => {
    const id = storagePrompt.invoiceId;
    if (!id) { closeStoragePrompt(); return; }
    let qs = '';
    if (storagePrompt.title) qs = `?title=${encodeURIComponent(storagePrompt.title)}`;
    qs += (qs ? '&' : '?') + `storageSummary=${summary ? 1 : 0}`;
    const url = `${API}/api/invoices/${id}/pdf${qs}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    closeStoragePrompt();
  };

  // ===== PRINT MULTIPLE (MERGED PDF) =====
  const mergeAndOpenSelectedInvoices = async () => {
    try {
      if (!selected.size) { alert('Select at least one invoice.'); return; }
      const ids = Array.from(selected);
      if (ids.length > 80 && !window.confirm(`Merge ${ids.length} PDFs into one file?`)) return;

      // Lazy-load pdf-lib to avoid adding weight until needed
      let PDFDocument;
      try {
        ({ PDFDocument } = await import('pdf-lib'));
      } catch (e) {
        console.error('Missing dependency pdf-lib', e);
        alert('Merging requires the pdf-lib package. Please install it with:\n\n  npm i pdf-lib\n\nThen retry.');
        return;
      }

      const merged = await PDFDocument.create();

      // Fetch and append each invoice PDF sequentially to keep memory stable
      for (const id of ids) {
        const row = data.find(r => r.invoiceId === id);
        let title = pdfTitleForType(row?.invoiceTypeName || row?.type || row?.invoiceType);
        const notesLower = String(row?.notes || '').toLowerCase();
        const typeLower = String(row?.invoiceTypeName || row?.type || row?.invoiceType || '').toLowerCase();
        if (typeLower.includes('storage') && notesLower.includes('weekly storage')) {
          title = 'Weekly Warehouse Storage Invoice';
        }
        const url = `${API}/api/invoices/${id}/pdf${title ? `?title=${encodeURIComponent(title)}` : ''}`;
        const res = await fetch(url);
        if (!res.ok) throw new Error(`Failed to fetch PDF for invoice ${id}`);
        const bytes = await res.arrayBuffer();
        const src = await PDFDocument.load(bytes);
        const pages = await merged.copyPages(src, src.getPageIndices());
        pages.forEach(p => merged.addPage(p));
      }

      const mergedBytes = await merged.save();
      const blob = new Blob([mergedBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);

      // Open a printable window and auto-trigger print reliably (with retries)
      const printWin = window.open('', '_blank', 'noopener,noreferrer');
      if (printWin) {
        const safeUrl = url + '#toolbar=0&navpanes=0&statusbar=0';
        const html = `<!doctype html><html><head><title>Merged Invoices</title>
          <meta charset="utf-8" />
          <style>html,body{margin:0;height:100%} #pf{border:0;width:100%;height:100%}</style>
        </head><body>
          <iframe id="pf" src="${safeUrl}"></iframe>
          <script>
            (function(){
              const iframe = document.getElementById('pf');
              let attempts = 0;
              function tryPrint(){
                attempts++;
                try {
                  const cw = iframe && iframe.contentWindow;
                  if (cw) { cw.focus(); cw.print(); }
                  else { window.print(); }
                } catch(e) {
                  // swallow and retry
                }
                if (attempts < 8) setTimeout(tryPrint, 800); // retry up to ~6s total
                else setTimeout(()=>{ window.close(); }, 1200);
              }
              iframe.addEventListener('load', () => setTimeout(tryPrint, 900));
              // safety: also kick off after a delay in case load doesn't fire
              setTimeout(tryPrint, 2000);
              window.addEventListener('afterprint', () => setTimeout(()=>{ window.close(); }, 600));
            })();
          <\/script>
        </body></html>`;
        printWin.document.open();
        printWin.document.write(html);
        printWin.document.close();
        // Revoke URL after a short delay to avoid memory leaks
        setTimeout(() => URL.revokeObjectURL(url), 120_000);
      } else {
        // Fallback: prompt download so the user can print from their PDF viewer
        const a = document.createElement('a');
        a.href = url;
        a.download = `invoices_merged_${Date.now()}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
    } catch (err) {
      console.error(err);
      alert(err.message || 'Failed to merge PDFs');
    }
  };

  return (
    <div className="invoices-wrapper">
      <div className="invoice-filters">
        {/* Search box (styled like Orders/Receipts) */}
        <div className="search" title={'Search by invoice #, doc #'}>
          <FaSearch />
          <input
            ref={searchRef}
            type="text"
            placeholder={'Search by invoice #, doc #'}
            value={q}
            onChange={e => setQ(e.target.value)}
          />
          {q && (
            <button className="clear" onClick={() => setQ('')} aria-label="Clear search">
              <FaTimes size={12}/>
            </button>
          )}
        </div>
        {/* Internal-only client name filter (separate) */}
        {!IS_CLIENT && (
          <div className="client-filter">
            <FaSearch />
            <input
              list="invoices-client-autocomplete"
              type="text"
              placeholder="Client name or #id"
              value={clientNameFilter}
              onChange={e => setClientNameFilter(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') fetchInvoices(0, meta.limit); }}
            />
            <datalist id="invoices-client-autocomplete">
              {filteredClientOptions.map(opt => (
                <option key={opt.id} value={`${opt.name} (#${opt.id})`} />
              ))}
            </datalist>
            {clientNameFilter && (
              <button className="clear" onClick={() => { setClientNameFilter(''); fetchInvoices(0, meta.limit); }} aria-label="Clear client filter">
                <FaTimes size={12} />
              </button>
            )}
          </div>
        )}
        <MultiSelectStatuses options={statuses} value={statusIds} onChange={setStatusIds} />
        <MultiSelectTypes options={types} value={typeIds} onChange={setTypeIds} />

        {/* Date Preset (like Orders) */}
        <label className="preset-controls" title="Quick date range">
          Preset:
          <select value={datePreset} onChange={e => applyDatePreset(e.target.value)}>
            <option value="">Custom</option>
            <option value="all">All</option>
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="thisWeek">This week</option>
            <option value="lastWeek">Last week</option>
            <option value="last7">Last 7 days</option>
            <option value="last30">Last 30 days</option>
            <option value="last90">Last 90 days</option>
            <option value="thisMonth">This month</option>
            <option value="lastMonth">Last month</option>
            <option value="ytd">Year to date</option>
          </select>
        </label>

        <label className="date-field">From
          <input type="date" value={from} onChange={e => { setFrom(e.target.value); setDatePreset(''); }} />
        </label>

        <label className="date-field">To
          <input type="date" value={to} onChange={e => { setTo(e.target.value); setDatePreset(''); }} />
        </label>

        {/* Action buttons on a new line */}
      <div className="toolbar-actions" style={{ width:'100%', display:'flex', gap: 8, flexWrap:'wrap', marginTop: 8 }}>
          {IS_CLIENT && !singleClientOption && (
            <div className="client-select">
              <label htmlFor="invoices-client-select" style={{ marginRight: 6 }}>Client</label>
              <select
                id="invoices-client-select"
                className="client-dropdown"
                value={selectedClientId || ''}
                onChange={(e) => {
                  const val = e.target.value;
                  setSelectedClientId(val);
                  setSelected(new Set());
                  fetchInvoices(0, meta.limit);
                }}
              >
                <option value="">— All —</option>
                {clientOptions.map(opt => (
                  <option key={opt.id} value={opt.id}>{opt.name}</option>
                ))}
              </select>
            </div>
          )}
          {!IS_CLIENT && (
            <button className="btn" onClick={() => navigate('/invoices/new')}>
              <FaPlus style={{ marginRight: 6 }} /> New
            </button>
          )}
          {!IS_CLIENT && (
            <button className="btn" onClick={openReceivePayment} title="Receive payment for selected invoices">
              <FaMoneyBillWave style={{ marginRight: 6 }}/>Receive Payment
            </button>
          )}
          <button className="btn" onClick={() => fetchInvoices(0, meta.limit)}>
            <FaSync style={{ marginRight: 6 }} /> Refresh
          </button>
          {!IS_CLIENT && (
            <button
              className="btn"
              onClick={openEmailInvoices}
              disabled={selected.size === 0}
              title={selected.size ? `Email ${selected.size} selected invoice(s)` : 'Select invoices to email'}
            >
              <FaEnvelopeOpenText style={{ marginRight: 6 }} /> Email Selected
            </button>
          )}
          {!IS_CLIENT && (
            <button
              className="btn"
              onClick={mergeAndOpenSelectedInvoices}
              disabled={selected.size === 0}
              title={selected.size ? `Merge ${selected.size} invoice PDF(s) into one` : 'Select invoices to merge'}
            >
              <FaPrint style={{ marginRight: 6 }} /> Print Selected (Merged)
            </button>
          )}
          {!IS_CLIENT && (
            <button
              className="btn"
              onClick={bulkMarkBadDebtSelected}
              disabled={selected.size === 0}
              title={selected.size ? `Mark ${selected.size} selected invoice(s) as bad debt` : 'Select invoices to mark as bad debt'}
            >
              <FaBomb style={{ marginRight: 6 }} /> Mark Bad Debt
            </button>
          )}
          {!IS_CLIENT && (
            <button
              className="btn"
              onClick={bulkCancelSelected}
              disabled={selected.size === 0}
              title={selected.size ? `Cancel ${selected.size} selected invoice(s)` : 'Select invoices to cancel'}
            >
              <FaBan style={{ marginRight: 6 }} /> Cancel Selected
            </button>
          )}
          <button className="btn" onClick={exportCsv}>
            <FaDownload style={{ marginRight: 6 }} /> Export CSV
          </button>
          {!IS_CLIENT && (
            <button className="btn" onClick={batchRecompute}>
              <FaSync style={{ marginRight: 6 }} /> Batch Recompute
            </button>
          )}
          <button className="btn" onClick={resetFilters} title="Clear filters and local storage">
            <FaUndo style={{ marginRight: 6 }} /> Reset
          </button>
          
          <div className="cols-controls">
            <button className="btn" ref={colsBtnRef} onClick={() => setColsOpen(v => !v)} title="Show/Hide columns"><FaColumns style={{ marginRight: 6 }} />Columns</button>
            {colsOpen && (
              <div className="cols-popover" ref={colsPopoverRef}>
                <div className="cols-head">Visible columns</div>
                <ul>
                  {COLUMN_DEFS.map(col => (
                    <li key={col.key}>
                      <label>
                        <input type="checkbox" checked={visibleCols.has(col.key)} onChange={() => toggleCol(col.key)} />
                        <span>{col.label}</span>
                      </label>
                    </li>
                  ))}
                </ul>
                <div className="cols-actions">
                  <button className="mini-btn" onClick={allCols}>All</button>
                  <button className="mini-btn" onClick={noneCols}>None</button>
                  <button className="mini-btn" onClick={() => setColsOpen(false)}>Close</button>
                </div>
              </div>
            )}
          </div>
        </div>

        

        
      </div>

      <div className="totals-bar">
        <div><strong>Total invoices:</strong> {Number(meta?.total || 0).toLocaleString()}</div>
        <div><strong>Total Amount:</strong> {money(totalAmount)}</div>
        <div><strong>Paid:</strong> {money(amountPaid)}</div>
        <div><strong>Due:</strong> {money(amountDue)}</div>
      </div>

      {loading ? (
        <div className="loading">Loading...</div>
      ) : (
        <div className="invoices-split-area">
        <Split
          direction="vertical"
          sizes={invBottomPaneMode === 'attachments' ? invSplitSizes : [100, 0]}
          minSize={invBottomPaneMode === 'attachments' ? [200, 200] : [200, 0]}
          gutterSize={invBottomPaneMode === 'attachments' ? 6 : 0}
          onDragEnd={(sizes) => { setInvSplitSizes(sizes); try { localStorage.setItem('invoices-split-sizes', JSON.stringify(sizes)); } catch {} }}
          className="invoices-vertical-split"
        >
          <div className="pane invoices-pane-top">
            <div className="invoice-table-wrapper">
              <table className="invoice-table" ref={invTableRef}>
              <colgroup>
                {(['select','actions', ...COLUMN_DEFS.filter(c => visibleCols.has(c.key)).map(c => c.key)]).map(k => (
                  <col key={k} style={{ width: invColWidths[k] ? `${invColWidths[k]}px` : undefined }} />
                ))}
              </colgroup>
              <thead>
                <tr ref={invHeaderRowRef}>
                  <th style={{ width: 28 }}>
                    <input
                      type="checkbox"
                      onClick={(e) => e.stopPropagation()}
                      onChange={toggleSelectAllOnPage}
                      checked={selected.size === data.length && data.length > 0}
                    />
                    <span className="col-resizer" onMouseDown={(e)=>startInvResize('select', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitInvCol('select'); }} />
                  </th>
                  <th>Actions
                    <span className="col-resizer" onMouseDown={(e)=>startInvResize('actions', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitInvCol('actions'); }} />
                  </th>
                  {COLUMN_DEFS.map(col => visibleCols.has(col.key) ? (
                    <React.Fragment key={col.key}>
                      {col.sort ? (
                        renderSortHeader(col.key, col.label, col.sort)
                      ) : (
                        <th data-colkey={col.key}>
                          {col.label}
                          <span
                            className="col-resizer"
                            onMouseDown={(e)=>startInvResize(col.key, e)}
                            onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }}
                            onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitInvCol(col.key); }}
                          />
                        </th>
                      )}
                    </React.Fragment>
                  ) : null)}
                </tr>
              </thead>
              <tbody>
                {data.length > 0 ? data.map((row) => {
                  const statusText = String(row.statusName || row.status || '').toLowerCase();
                  const isPaid = statusText === 'paid';
                  const paymentsArray = Array.isArray(row.payments) ? row.payments : [];
                  const isActive = row.invoiceId === activeId;
                  const statusSlug = statusSlugOf(row);

                  return (
                    <tr
                      key={row.invoiceId}
                      data-row={row.invoiceId}
                      onClick={() => onRowClick(row)}
                      className={isActive ? 'active-row' : ''}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>
                        <input
                          type="checkbox"
                          checked={selected.has(row.invoiceId)}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggleSelect(row.invoiceId)}
                        />
                      </td>

                      <td className="actions-cell" onClick={(e) => e.stopPropagation()}>
                        {/* View first */}
                        <button
                          type="button"
                          className="mini-btn"
                          title="View"
                          onClick={() => { ensureRowActive(row.invoiceId); openViewInvoice(row.invoiceId); }}
                        >
                          <FaEye size={16} />
                        </button>

                        {/* Then Edit (internal users only) */}
                        {!IS_CLIENT && (
                          <>
                            <button
                              className="mini-btn"
                              title="Edit"
                              onClick={() => { ensureRowActive(row.invoiceId); navigate(`/invoice/${row.invoiceId}/edit`); }}
                            >
                              <FaEdit size={16} />
                            </button>
                            <button
                              className="mini-btn"
                              title="Duplicate Invoice"
                              onClick={(e) => { e.stopPropagation(); ensureRowActive(row.invoiceId); duplicateInvoice(row.invoiceId); }}
                            >
                              <FaCopy size={16} />
                            </button>
                          </>
                        )}

                        <button
                          type="button"
                          className="mini-btn"
                          title="PDF"
                          onClick={() => {
                            ensureRowActive(row.invoiceId);
                            let title = pdfTitleForType(row.invoiceTypeName || row.type || row.invoiceType);
                            const tRaw = String(row.invoiceTypeName || row.type || row.invoiceType || '').toLowerCase();
                            const notesLower = String(row?.notes || '').toLowerCase();
                            if (tRaw.includes('storage') && notesLower.includes('weekly storage')) {
                              title = 'Weekly Warehouse Storage Invoice';
                            }
                            if (tRaw.includes('storage')) { openStoragePrompt(row.invoiceId, title); return; }
                            let qs = title ? `?title=${encodeURIComponent(title)}` : '';
                            if (tRaw.includes('storage')) {
                              const wantSummary = window.confirm('Summarize storage charges?\nOK = Summarize, Cancel = Show Details');
                              if (qs) qs += `&storageSummary=${wantSummary?1:0}`; else qs = `?storageSummary=${wantSummary?1:0}`;
                            }
                            const url = `${API}/api/invoices/${row.invoiceId}/pdf${qs}`;
                            window.open(url, '_blank', 'noopener,noreferrer');
                          }}
                        >
                          <FaFilePdf size={16} />
                        </button>

                        <button
                          type="button"
                            className="mini-btn"
                            title="Export invoice charges to CSV"
                            onClick={(e) => {
                              e.stopPropagation();
                              ensureRowActive(row.invoiceId);
                              const url = `${API}/api/invoices/${row.invoiceId}/charges/export`;
                              window.open(url, '_blank', 'noopener,noreferrer');
                            }}
                          >
                            <FaFileCsv size={16} />
                        </button>

                        {!IS_CLIENT && (
                          <button
                            type="button"
                            className="mini-btn"
                            title="Email Invoice"
                            onClick={() => { ensureRowActive(row.invoiceId); openEmailInvoice(row.invoiceId); }}
                          >
                            <FaEnvelope size={16} />
                          </button>
                        )}
                        {/* Attachments action (green when present) */}
                        <button
                          type="button"
                          className="mini-btn"
                          title="Invoice Attachments"
                          onClick={(e) => {
                            e.stopPropagation();
                            ensureRowActive(row.invoiceId);
                            setInvBottomPaneMode('attachments');
                          }}
                          style={{ color: Number(row.attachments_count || 0) > 0 ? '#21bb50' : undefined }}
                        >
                          <FaPaperclip size={16} />
                        </button>
                        <button
                          type="button"
                          className="mini-btn"
                          title="Invoice History"
                          onClick={(e) => {
                            e.stopPropagation();
                            ensureRowActive(row.invoiceId);
                            openInvoiceHistory(row.invoiceId);
                          }}
                        >
                          <FaHistory size={16} />
                        </button>
                        {!IS_CLIENT && !isPaid && (
                          <button
                            type="button"
                            className="mini-btn"
                            title="Receive Payment"
                            onClick={(e) => {
                              e.stopPropagation();
                              ensureRowActive(row.invoiceId);
                              openReceivePaymentSingle(row);
                            }}
                          >
                            <FaMoneyBillWave size={16} style={{ color: '#21bb50' }} />
                          </button>
                        )}
                        {!IS_CLIENT && !isPaid && (
                          <button
                            type="button"
                            className="mini-btn danger"
                            title="Cancel Invoice"
                            onClick={() => { ensureRowActive(row.invoiceId); cancelInvoice(row.invoiceId); }}
                          >
                            <FaBan size={16} />
                          </button>
                        )}
                      </td>

                      {visibleCols.has('invoiceId') && (
                        <td>
                          <Link
                            to={`/invoice/${row.invoiceId}`}
                            className="invoice-link"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {row.invoiceId}
                          </Link>
                        </td>
                      )}
                      {visibleCols.has('status') && (
                        <td className={`status ${isPaid ? 'paid' : 'unpaid'} ${statusSlug}`}>
                          {row.statusName || row.status}
                        </td>
                      )}
                      {visibleCols.has('clientName') && (
                        <td className="nowrap">{row.clientName}</td>
                      )}
                      {visibleCols.has('invoiceDate') && (
                        <td>{new Date(row.invoiceDate).toLocaleDateString()}</td>
                      )}
                      {visibleCols.has('invoiceType') && (
                        <td>{(() => {
                          const base = row.invoiceTypeName || row.type || row.invoiceType || '';
                          const isStorage = String(base).toLowerCase().includes('storage');
                          const notesLower = String(row?.notes || '').toLowerCase();
                          if (isStorage && notesLower.includes('weekly storage')) return 'Weekly';
                          return base;
                        })()}</td>
                      )}
                      {visibleCols.has('docRef') && (
                        <td className="nowrap">
                          {(() => {
                            const typeId = Number(row.invoiceTypeId || 0);
                            const rid = Number(row.receiptId || 0);
                            const oid = Number(row.orderId || 0);
                            const eid = Number(row.eorderId || 0);

                            // For periodic invoices: coalesce mapped refs (from overview payload)
                            const recs = Array.isArray(row.receiptIds) ? row.receiptIds : (rid ? [rid] : []);
                            const ords = Array.isArray(row.orderIds)   ? row.orderIds   : (oid ? [oid] : []);
                            const eords= Array.isArray(row.eorderIds)  ? row.eorderIds  : (eid ? [eid] : []);
                            const eordLabel = (id) => {
                              const num = row.eorderNumberById?.[id];
                              return num ? `E-Order ${num}` : `E-Order #${id}`;
                            };
                            const labels = [
                              ...recs.map(id => ({ k: `WR #${id}`, type: 'wr', id })),
                              ...ords.map(id => ({ k: `Order #${id}`, type: 'ord', id })),
                              ...eords.map(id => ({ k: eordLabel(id), type: 'eord', id })),
                            ];

                            const linkFor = (o) => {
                              if (!o) return '#';
                              if (o.type === 'wr') {
                                // Client users cannot access /receive/:id (internal-only); open the Receipt PDF instead.
                                return IS_CLIENT ? `${API}/api/receipts/${encodeURIComponent(o.id)}/pdf` : `/receive/${o.id}`;
                              }
                              if (o.type === 'ord') return `/orders?q=OR${o.id}`;
                              if (o.type === 'eord') return `/eorders?q=${o.id}`;
                              return '#';
                            };

                            const DocLink = ({ o, className, ...rest }) => {
                              const href = linkFor(o);
                              const cls = ['invoice-link', className].filter(Boolean).join(' ');
                              // Client portal: open receipts (WR) in a new tab.
                              if (IS_CLIENT && o?.type === 'wr') {
                                return (
                                  <a
                                    href={href}
                                    className={cls}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    {...rest}
                                  >
                                    {o.k}
                                  </a>
                                );
                              }
                              return (
                                <Link
                                  to={href}
                                  className={cls}
                                  onClick={(e) => e.stopPropagation()}
                                  {...rest}
                                >
                                  {o.k}
                                </Link>
                              );
                            };

                            // Show all associated docs (coalesced set) for all invoice types
                            const show = labels;

                            const maxInline = 2;
                            const inline = show.slice(0, maxInline);
                            const overflow = show.slice(maxInline);

                            return (
                              <div
                                style={docPopoverStyles.container}
                                onMouseEnter={() => startDocHover(row.invoiceId)}
                                onMouseLeave={endDocHover}
                              >
                                {show.length === 0 ? (
                                  ''
                                ) : (
                                  <>
                                    {inline.map((o, idx) => (
                                      <React.Fragment key={`${o.type}-${o.id}`}>
                                        <DocLink o={o} />
                                        {idx < inline.length - 1 ? ', ' : ''}
                                      </React.Fragment>
                                    ))}
                                    {overflow.length > 0 && (
                                      <>
                                        {inline.length ? ', ' : ''}
                                        <span style={docPopoverStyles.faded}>…</span>
                                      </>
                                    )}
                                  </>
                                )}

                                {docHoverId === row.invoiceId && overflow.length > 0 && (
                                  <div
                                    style={docPopoverStyles.bubble}
                                    onMouseEnter={() => startDocHover(row.invoiceId)}
                                    onMouseLeave={endDocHover}
                                  >
                                    <div style={{ fontWeight: 600, marginBottom: 6 }}>Associated Docs</div>
                                    <div style={docPopoverStyles.list}>
                                      {show.map(o => (
                                        <DocLink
                                          key={`${o.type}-${o.id}`}
                                          o={o}
                                          style={docPopoverStyles.listItem}
                                        />
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                      )}
                      {visibleCols.has('totalAmount') && (
                        <td className="num">{money(row.totalAmount)}</td>
                      )}
                      {visibleCols.has('amountPaid') && (
                        <td className="num">{money(row.amountPaid)}</td>
                      )}
                      {visibleCols.has('amountDue') && (
                        <td className="num">{money(row.amountDue)}</td>
                      )}

                      {visibleCols.has('paymentCount') && (
                        <td>
                          <button
                            className="mini-btn"
                            title="View payments"
                            onClick={(e) => { e.stopPropagation(); openPayments(row.invoiceId, e.currentTarget); }}
                            onMouseEnter={(e) => { cancelClose(); openPayments(row.invoiceId, e.currentTarget); }}
                            onMouseLeave={scheduleClose}
                            style={{ minWidth: 44 }}
                          >
                            {Number(row.paymentCount || 0).toLocaleString()}
                          </button>
                          {paymentsOpenFor === row.invoiceId && (
                            <PaymentsPopover
                              open
                              anchorRect={paymentsAnchor}
                              onClose={closePayments}
                              payments={paymentsArray}
                              invoiceId={row.invoiceId}
                              onMouseEnter={cancelClose}
                              onMouseLeave={scheduleClose}
                            />
                          )}
                        </td>
                      )}

                      {visibleCols.has('lastPaymentDate') && (
                        <td>{row.lastPaymentDate ? new Date(row.lastPaymentDate).toLocaleDateString() : ''}</td>
                      )}

                      {visibleCols.has('createdBy') && (
                        <td className="nowrap">{row.createdBy || ''}</td>
                      )}

                      {visibleCols.has('notes') && (
                        <td className="nowrap">{row.notes || ''}</td>
                      )}
                    </tr>
                  );
                }) : (
                  <tr><td colSpan={2 + COLUMN_DEFS.filter(c => visibleCols.has(c.key)).length}>No invoices found.</td></tr>
                )}
              </tbody>

              {page === lastPage && (
                <tfoot>
                  <tr className="totals-row">
                    <td></td>
                    <td></td>
                    {COLUMN_DEFS.map(col => {
                      if (!visibleCols.has(col.key)) return null;
                      let content = '';
                      if (col.key === 'totalAmount') content = money(meta?.totals?.totalAmount || 0);
                      if (col.key === 'amountPaid')  content = money(meta?.totals?.amountPaid  || 0);
                      if (col.key === 'amountDue')   content = money(meta?.totals?.amountDue   || 0);
                      return <td key={`tot-${col.key}`} className={col.cls || ''}>{content}</td>;
                    })}
                  </tr>
                </tfoot>
              )}
              </table>
            </div>

            <div className="invoice-pagination">
              <button className="btn" disabled={page <= 1} onClick={() => gotoPage(1)}>&laquo;</button>
              <button className="btn" disabled={page <= 1} onClick={() => gotoPage(page - 1)}>Prev</button>
              <span>Page {page} of {lastPage}</span>
              <button className="btn" disabled={page >= lastPage} onClick={() => gotoPage(page + 1)}>Next</button>
              <button className="btn" disabled={page >= lastPage} onClick={() => gotoPage(lastPage)}>&raquo;</button>

            <label style={{ marginLeft: 12, fontSize: 13 }}>
              Rows per page:&nbsp;
              <input
                type="number"
                min={1}
                max={10000}
                value={limitInput}
                style={{ width: 75, fontSize: 13, padding: '2px 4px' }}
                onKeyDown={e => { setIsTypingLimit(true); if (e.key === 'Enter') { setIsTypingLimit(false); handleLimitUpdate(e.currentTarget.value); } }}
                onChange={e => { setLimitInput(e.target.value); if (!isTypingLimit) { handleLimitUpdate(e.target.value); } }}
                onBlur={e => { setIsTypingLimit(false); handleLimitUpdate(e.target.value); }}
              />
            </label>
            </div>

          </div>

          <div className="pane invoices-pane-bottom">
            {invBottomPaneMode === 'attachments' && activeId && (
              <div className="invoice-attachments-panel">
                <div className="bottom-pane-toolbar" style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
                  <button
                    className={`btn toggle ${invBottomPaneMode === 'attachments' ? 'active' : ''}`}
                    onClick={() => setInvBottomPaneMode('attachments')}
                    aria-pressed={invBottomPaneMode === 'attachments'}
                    title="Attachments"
                  >
                    <FaPaperclip style={{ verticalAlign: '-2px' }} />
                    <span style={{ marginLeft: 6 }}>Attachments</span>
                  </button>
                  <div style={{ flex: 1 }} />
                  <button className="btn" onClick={() => setInvBottomPaneMode(null)} title="Close">
                    <FaTimes style={{ marginRight: 6 }} /> Close
                  </button>
                </div>
                <h3 style={{ margin:0, marginBottom: 8 }}>Invoice Attachments (#{activeId})</h3>

                <div
                  className={`attachments-drop ${isDragging ? 'dragging' : ''}`}
                  onDrop={(e) => { e.preventDefault(); setIsDragging(false); const f = e.dataTransfer?.files; if (f && f.length) setPendingFiles(Array.from(f)); }}
                  onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                  onDragLeave={() => setIsDragging(false)}
                  style={{ padding:12, border:'1px dashed #aaa', borderRadius:6, marginBottom:12, background: isDragging ? 'rgba(0,0,0,0.05)' : 'transparent' }}
                >
                  <div className="attachments-toolbar" style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
                    <input
                      type="text"
                      placeholder="Description (optional)"
                      value={attachDesc}
                      onChange={(e) => setAttachDesc(e.target.value)}
                      style={{ flex:'1 1 240px', minWidth: 220 }}
                    />
                    <input ref={fileInputRef} type="file" multiple style={{ display:'none' }} onChange={(e) => setPendingFiles(Array.from(e.target.files || []))} />
                    <button className="btn" type="button" onClick={() => fileInputRef.current?.click?.()} title="Choose files to upload">
                      <FaPaperclip style={{ verticalAlign:'-2px', marginRight: 6 }} />
                      Choose Files
                    </button>
                    <button className="btn" type="button" onClick={uploadAttachmentFiles} disabled={!pendingFiles.length} title="Upload selected files">
                      <FaUpload style={{ marginRight: 6 }} />
                      Upload
                    </button>
                    <button className="btn" type="button" onClick={() => fetchInvoiceAttachments(activeId)} title="Refresh">
                      <FaSync style={{ marginRight: 6 }} />
                      Refresh
                    </button>
                  </div>

                  {!!pendingFiles.length && (
                    <div className="pending-files" style={{ marginTop: 8 }}>
                      {pendingFiles.map((f, i) => (
                        <span key={f.name + i} className="file-chip" style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'2px 6px', border:'1px solid #ddd', borderRadius: 12, marginRight:6, marginBottom:6 }}>
                          {f.name}
                          <button type="button" className="chip-x" title="Remove" aria-label={`Remove ${f.name}`} onClick={() => setPendingFiles(prev => prev.filter((_, idx) => idx !== i))} style={{ border:'none', background:'transparent', cursor:'pointer' }}>×</button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="table-wrapper">
                  <table className="table">
                    <thead>
                      <tr>
                        <th style={{ width:72 }}>Action</th>
                        <th>Filename</th>
                        <th>Date</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invAttachmentsLoading ? (
                        <tr><td colSpan={4}>Loading…</td></tr>
                      ) : invAttachments.length ? (
                        invAttachments.map(a => {
                          const name = (a.filename || '').split('/').pop();
                          return (
                            <tr key={a.id ?? name}>
                              <td>
                                {a.id ? (
                                  <button className="mini-btn danger" title="Delete attachment" onClick={() => deleteInvoiceAttachment(a.id)}>Delete</button>
                                ) : null}
                              </td>
                              <td><a href={`${API}/api/invoices/${activeId}/attachments/${encodeURIComponent(name)}`} target="_blank" rel="noopener noreferrer">{name}</a></td>
                              <td>{a.date ? new Date(a.date).toLocaleString() : ''}</td>
                              <td>{a.description || ''}</td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr><td colSpan={4} style={{ color:'#999' }}>No attachments</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </Split>
        </div>
      )}

      {/* ===== Receive Payment Modal ===== */}
      {/* ===== View Invoice Modal ===== */}
      {viewOpen && (
        <div className="modal-overlay" onMouseDown={(e)=>{ if (e.target===e.currentTarget) setViewOpen(false); }}>
          <div className="modal" role="dialog" aria-modal="true" aria-label="View Invoice" onMouseDown={(e)=>e.stopPropagation()}>
            <h2 style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <span>View Invoice{viewData?.header?.id ? ` #${viewData.header.id}` : ''}</span>
              <button type="button" className="mini-btn" onClick={()=>setViewOpen(false)} title="Close" aria-label="Close dialog"><FaTimes /></button>
            </h2>
            {viewLoading ? (
              <div>Loading…</div>
            ) : viewData ? (
              <div className="modal-scroll">
                {/* Header summary */}
                <div style={{ display:'grid', gridTemplateColumns:'repeat(2, minmax(0, 1fr))', gap:'8px', marginBottom:'8px' }}>
                  <div><strong>Client:</strong> {viewData.header?.clientName} (#{viewData.header?.clientid})</div>
                  <div><strong>Date:</strong> {new Date(viewData.header?.date).toLocaleString()}</div>
                  <div><strong>Status:</strong> {viewData.header?.statusName || viewData.header?.status}</div>
                  <div><strong>Terms:</strong> {viewData.header?.clientPaymentTerms || ''}</div>
                  <div><strong>Order ID:</strong> {viewData.header?.orderid || '-'}</div>
                  <div><strong>Receipt ID:</strong> {viewData.header?.receiptid || '-'}</div>
                  <div><strong>eOrder ID:</strong> {viewData.header?.eorderid || '-'}</div>
                  <div style={{ gridColumn: '1 / -1' }}><strong>Notes:</strong> {viewData.header?.notes || ''}</div>
                </div>

                {/* Totals */}
                <div style={{ display:'flex', gap:'1.5rem', margin: '6px 0 10px' }}>
                  <div><strong>Total:</strong> {money(viewData.computed?.totalAmount)}</div>
                  <div><strong>Paid:</strong> {money(viewData.computed?.amountPaid)}</div>
                  <div><strong>Balance:</strong> {money(viewData.computed?.amountDue)}</div>
                </div>

                {/* Line items */}
                <div className="table-wrapper" style={{ marginTop: 6 }}>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Description</th>
                        <th>Date</th>
                        <th>Warehouse</th>
                        <th>Charge Name</th>
                        <th>Qty</th>
                        <th>Price Each</th>
                        <th>Amt Due</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(viewData.items || []).map(it => {
                        const qty = Number(it.qty) || 0;
                        const rate = Number(it.amt) || 0;
                        const amt  = qty * rate;
                        return (
                          <tr key={it.id}>
                            <td>{it.description || ''}</td>
                            <td>{dateOnly(it.docDate)}</td>
                            <td>{it.warehouseName || ''}</td>
                            <td>{it.chargename || ''}</td>
                            <td className="num">{qty.toLocaleString()}</td>
                            <td className="num">{money(rate)}</td>
                            <td className="num">{money(amt)}</td>
                          </tr>
                        );
                      })}
                      {!(viewData.items || []).length && (
                        <tr><td colSpan={7}>No items</td></tr>
                      )}
                    </tbody>
                    {Array.isArray(viewData.items) && viewData.items.length > 0 && (
                      <tfoot>
                        <tr>
                          <td colSpan={6} style={{ textAlign: 'right', fontWeight: 700 }}>Total</td>
                          <td className="num" style={{ fontWeight: 700 }}>{money(viewData.computed?.totalAmount)}</td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>

              </div>
            ) : (
              <div>No data.</div>
            )}

            {/* Actions at bottom (stay visible while body scrolls) */}
            <div className="modal-actions" style={{ justifyContent:'flex-end' }}>
              <button
                className="btn"
                onClick={() => {
                  if (!viewData?.header?.id) return;
                  const t = (viewData?.header?.invoiceTypeName || viewData?.header?.type || '');
                  const title = pdfTitleForType(t);
                  const isStorage = String(t || '').toLowerCase().includes('storage');
                  if (isStorage) { openStoragePrompt(viewData.header.id, title); return; }
                  let qs = title ? `?title=${encodeURIComponent(title)}` : '';
                  if (isStorage) {
                    const wantSummary = window.confirm('Summarize storage charges?\nOK = Summarize, Cancel = Show Details');
                    if (qs) qs += `&storageSummary=${wantSummary?1:0}`; else qs = `?storageSummary=${wantSummary?1:0}`;
                  }
                  const url = `${API}/api/invoices/${viewData.header.id}/pdf${qs}`;
                  window.open(url, '_blank', 'noopener,noreferrer');
                }}
                disabled={!viewData?.header?.id}
              >
                <FaFilePdf /> Open PDF
              </button>
              <button className="btn primary" onClick={()=>setViewOpen(false)} aria-label="Close viewer">
                <FaTimes style={{ marginRight: 6 }} /> Close
              </button>
            </div>
          </div>
        </div>
      )}

      {payOpen && (
        <div className="modal-overlay">
          <div className="modal">
            <h2><FaMoneyBillWave style={{ marginRight: 8 }}/> Receive Payment</h2>

            <form className="payment-form" onSubmit={submitReceivePayment}>
              <div className="modal-scroll">
                <div className="form-grid">
                  <label>Client
                    <input
                      type="text"
                      value={payForm.clientName}
                      disabled
                    />
                  </label>

                  <input type="hidden" value={payForm.clientid} readOnly />

                  <label>Date
                    <input
                      type="date"
                      value={payForm.date}
                      onChange={e => setPayForm(f => ({ ...f, date: e.target.value }))}
                    />
                  </label>

                  <label>Payment Type
                    <select
                      value={payForm.paymenttype}
                      onChange={e => setPayForm(f => ({ ...f, paymenttype: e.target.value }))}
                    >
                      <option value="">-- Select --</option>
                      {paymentTypes.map(pt => (
                        <option key={pt.id} value={pt.id}>{pt.name}</option>
                      ))}
                    </select>
                  </label>

                  <label>Total Amount
                    <input
                      type="number"
                      step="0.01"
                      value={payForm.amt}
                      onChange={e => setPayForm(f => ({ ...f, amt: e.target.value }))}
                      disabled={isCreditPayment}
                    />
                  </label>

                  <label>Reference
                    <input
                      type="text"
                      value={payForm.reference}
                      onChange={e => setPayForm(f => ({ ...f, reference: e.target.value }))}
                    />
                  </label>

                  <label>Notes
                    <input
                      type="text"
                      value={payForm.notes}
                      onChange={e => setPayForm(f => ({ ...f, notes: e.target.value }))}
                    />
                  </label>
                </div>

                {isCreditPayment && (
                  <div className="credit-section">
                    <div className="credit-summary">
                      <div><strong>Credits available:</strong> {money(totalCreditAvailable)}</div>
                      <div><strong>Selected credit:</strong> {money(selectedCreditTotal)}</div>
                      {creditInsufficient && (
                        <div className="credit-warning">Selected credits do not fully cover the applied amount. A partial credit will be applied.</div>
                      )}
                      {creditError && (
                        <div className="credit-warning">{creditError}</div>
                      )}
                    </div>
                    <div className="table-wrapper credit-table-wrapper">
                      <table className="table credit-table">
                        <thead>
                          <tr>
                            <th style={{ width: 32 }}>
                              <input
                                type="checkbox"
                                checked={creditRows.length > 0 && selectedCreditIds.size === creditRows.length}
                                onChange={e => {
                                  const checked = e.target.checked;
                                  setSelectedCreditIds(checked ? new Set(creditRows.map(r => r.paymentid)) : new Set());
                                }}
                              />
                            </th>
                            <th>Credit Amount Avail</th>
                            <th>Date of original payment</th>
                            <th>Payment ID</th>
                          </tr>
                        </thead>
                        <tbody>
                          {creditLoading ? (
                            <tr><td colSpan={4}>Loading credits…</td></tr>
                          ) : creditRows.length ? (
                            creditRows.map(r => (
                              <tr key={r.paymentid}>
                                <td>
                                  <input
                                    type="checkbox"
                                    checked={selectedCreditIds.has(r.paymentid)}
                                    onChange={() => {
                                      setSelectedCreditIds(prev => {
                                        const next = new Set(prev);
                                        if (next.has(r.paymentid)) next.delete(r.paymentid);
                                        else next.add(r.paymentid);
                                        return next;
                                      });
                                    }}
                                  />
                                </td>
                                <td className="num">{money(r.available || 0)}</td>
                                <td>{r.date ? new Date(r.date).toLocaleDateString() : ''}</td>
                                <td className="nowrap">{r.paymentid}</td>
                              </tr>
                            ))
                          ) : (
                            <tr><td colSpan={4}>No available credits found.</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <div className="table-wrapper">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Invoice #</th>
                        <th>Date</th>
                        <th>Client</th>
                        <th style={{ textAlign:'right' }}>Due</th>
                        <th style={{ textAlign:'right' }}>Apply</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payForm.allocations.map((a, idx) => (
                        <tr key={a.invoiceid}>
                          <td>#{a.invoiceid}</td>
                          <td>{a.invoiceDate ? new Date(a.invoiceDate).toLocaleDateString() : ''}</td>
                          <td className="nowrap">{a.clientName}</td>
                          <td className="num">{money(a.amountDue || 0)}</td>
                          <td className="num">
                            <input
                              type="number"
                              step="0.01"
                              value={a.apply}
                              onChange={e => {
                                const val = Number(e.target.value || 0);
                                setPayForm(f => {
                                  const next = [...f.allocations];
                                  next[idx] = { ...next[idx], apply: val < 0 ? 0 : val };
                                  return { ...f, allocations: next };
                                });
                              }}
                              style={{ width: 120, textAlign:'right' }}
                            />
                          </td>
                        </tr>
                      ))}
                      {!payForm.allocations.length && (
                        <tr><td colSpan={5}>No invoices selected.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="modal-actions" style={{ justifyContent:'space-between' }}>
                <div style={{ opacity:.9 }}><strong>Total applied:</strong> {money(totalApplied)}</div>
                <div style={{ display:'flex', gap:8 }}>
                  <button type="button" className="btn" onClick={()=> setPayOpen(false)}>Cancel</button>
                  <button
                    type="submit"
                    className="btn primary"
                    disabled={isCreditPayment && (creditLoading || creditSelectionEmpty)}
                  >
                    Save Payment
                  </button>
                </div>
              </div>
            </form>

          </div>
        </div>
      )}

      {/* ===== Storage Print Prompt Modal ===== */}
      {storagePrompt.open && (
        <div className="modal-overlay" onMouseDown={(e)=>{ if (e.target===e.currentTarget) closeStoragePrompt(); }}>
          <div
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="storagePrintTitle"
            onMouseDown={e=>e.stopPropagation()}
            style={{ width: '50vw', maxWidth: '50vw' }}
          >
            <div className="modal-header">
              <h2 id="storagePrintTitle" style={{ margin: 0 }}>Print Storage Charges</h2>
              <button className="mini-btn" onClick={closeStoragePrompt} title="Close" aria-label="Close">
                <FaTimes size={16} />
              </button>
            </div>
            <div className="modal-body">
              <p style={{ marginTop: 0 }}>
                How would you like to print the storage charges?
              </p>
              <ul style={{ marginTop: 8, paddingLeft: 18 }}>
                <li><strong>Summarized</strong>: Group by Storage Unit and show a single total per unit.</li>
                <li><strong>Detailed</strong>: Show WR #, Receive Date, and each storage line item.</li>
              </ul>
            </div>
            <div className="modal-actions" style={{ justifyContent:'flex-end' }}>
              <button className="btn" onClick={() => launchStoragePdf(false)}>Show Details</button>
              <button className="btn primary" onClick={() => launchStoragePdf(true)}>Summarize</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== Email Invoice Modal ===== */}
      {emailOpen && (
        <EmailModal
          open={emailOpen}
          title={`Email Invoice #${emailForm.invoiceId}`}
          initialTo={emailForm.to}
          initialCc={emailForm.cc}
          initialBcc={emailForm.bcc}
          initialSubject={emailForm.subject || `Invoice #${emailForm.invoiceId}`}
          initialMessage={emailForm.message}
          allowCcBcc={true}
          ccPlaceholder=""
          bccPlaceholder=""
          toName="invoice_email_to"
          ccName="invoice_email_cc"
          bccName="invoice_email_bcc"
          toAutoComplete="email"
          ccAutoComplete="email"
          bccAutoComplete="email"
          attachments={emailModalAttachments}
          showAttachmentsToggle={true}
          attachmentsToggleLabel="Load attachments"
          attachmentsToggleChecked={emailAttachmentsEnabled}
          onAttachmentsToggle={handleEmailAttachmentsToggle}
          attachmentsLoading={emailAttachmentsLoading}
          extraContent={(function(){
            const row = data.find(r => r.invoiceId === emailForm.invoiceId) || {};
            const isStorage = String(row?.invoiceTypeName || row?.type || row?.invoiceType || '').toLowerCase().includes('storage');

            // Determine linked Orders/Receipts and whether any attachments exist for each
            const recs = Array.isArray(row.receiptIds) ? row.receiptIds : (Number(row.receiptId) ? [Number(row.receiptId)] : []);
            const ords = Array.isArray(row.orderIds)   ? row.orderIds   : (Number(row.orderId)   ? [Number(row.orderId)]   : []);
            const hasOrderAtt   = (emailModalAttachments || []).some(a => String(a?.source) === 'order');
            const hasReceiptAtt = (emailModalAttachments || []).some(a => String(a?.source) === 'receipt');

            const showNoOrderMsg   = ords.length > 0 && !hasOrderAtt;
            const showNoReceiptMsg = recs.length > 0 && !hasReceiptAtt;

            const blocks = [];
            if (isStorage) {
              blocks.push(
                <div key="stor-opts" style={{ marginTop: 8 }}>
                  <strong>Storage Invoice Options</strong>
                  <div style={{ display:'flex', gap:16, marginTop:6 }}>
                    <label style={{ display:'inline-flex', alignItems:'center', gap:6 }}>
                      <input type="radio" name="storEmailPrint" checked={emailStorageChoice==='summary'} onChange={()=>setEmailStorageChoice('summary')} /> Summarize charges
                    </label>
                    <label style={{ display:'inline-flex', alignItems:'center', gap:6 }}>
                      <input type="radio" name="storEmailPrint" checked={emailStorageChoice==='detail'} onChange={()=>setEmailStorageChoice('detail')} /> Show details
                    </label>
                  </div>
                </div>
              );
            }

            if (showNoOrderMsg || showNoReceiptMsg) {
              blocks.push(
                <div key="att-notes" style={{ marginTop: 10, padding: '8px 10px', border: '1px dashed #bbb', borderRadius: 6, background: 'rgba(0,0,0,0.02)' }}>
                  <strong>Related Attachments</strong>
                  <div style={{ marginTop: 6, display:'flex', flexDirection:'column', gap:4 }}>
                    {showNoOrderMsg && (
                      <div style={{ fontSize: 13, opacity: .9 }}>No Order attachments found for linked Orders.</div>
                    )}
                    {showNoReceiptMsg && (
                      <div style={{ fontSize: 13, opacity: .9 }}>No Warehouse Receipt attachments found for linked Receipts.</div>
                    )}
                  </div>
                </div>
              );
            }

            return blocks.length ? <>{blocks}</> : null;
          })()}
          onClose={() => setEmailOpen(false)}
          onSend={async ({ to, cc, bcc, subject, message, attachmentsSelected }) => {
            setEmailSending(true);
            try {
              const names = Array.isArray(attachmentsSelected) ? attachmentsSelected : [];
              const row = data.find(r => r.invoiceId === emailForm.invoiceId) || {};
              const isStorage = String(row?.invoiceTypeName || row?.type || row?.invoiceType || '').toLowerCase().includes('storage');
              // Prefer server-side attachment resolution (more reliable than downloading blobs in the browser).
              const selectedSet = new Set((names || []).map(x => String(x)));
              const selRecords = (emailModalAttachments || []).filter(rec => {
                const k = String(rec?.emailKey || '');
                if (k && selectedSet.has(k)) return true;
                // Back-compat fallback (older selection behavior used basename)
                const base = String(rec?.filename || '').split('/').pop();
                return !!base && selectedSet.has(base);
              });
              // Only pass selectedAttachmentNames for invoice-sourced files; order/receipt files are sent via refs.
              const invoiceOnlyNames = selRecords
                .filter(rec => String(rec?.source) === 'invoice')
                .map(rec => String(rec.filename || '').split('/').pop());
              // Also pass lightweight refs for order/receipt files so server can attach from disk (more reliable)
              const orderAttachmentRefs = selRecords
                .filter(rec => String(rec?.source) === 'order')
                .map(rec => ({ orderid: Number(rec.orderid), filename: String(rec.filename || '').split('/').pop() }));
              const receiptAttachmentRefs = selRecords
                .filter(rec => String(rec?.source) === 'receipt')
                .map(rec => ({ receiptid: Number(rec.receiptid), filename: String(rec.filename || '').split('/').pop() }));
              const payload = {
                to, cc, bcc, subject, message,
                userFullName: getUserFullName() || 'system',
                selectedAttachmentNames: invoiceOnlyNames,
                // Keep attachments[] empty; backend will attach from disk via refs/names.
                attachments: [],
                selectedOrderAttachmentRefs: orderAttachmentRefs,
                selectedReceiptAttachmentRefs: receiptAttachmentRefs,
              };
              if (isStorage) payload.storageSummary = (emailStorageChoice === 'summary');
              const res = await fetch(`${API}/api/invoices/${emailForm.invoiceId}/email`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
              });
              const j = await res.json();
              if (!res.ok) throw new Error(j?.error || 'Failed to send invoice email');
              markInvoicesAsSent(emailForm.invoiceId);
              setEmailOpen(false);
              try {
                const dbg = j?.attachmentsDebug;
                const missInv = Array.isArray(dbg?.missing?.invoiceNames) ? dbg.missing.invoiceNames.length : 0;
                const missOrd = Array.isArray(dbg?.missing?.orderRefs) ? dbg.missing.orderRefs.length : 0;
                const missRec = Array.isArray(dbg?.missing?.receiptRefs) ? dbg.missing.receiptRefs.length : 0;
                const missTotal = missInv + missOrd + missRec;
                if (missTotal > 0) {
                  console.warn('Invoice email sent but some selected attachments were missing:', dbg);
                  showToast({
                    id: `invoice.email.missing.${emailForm.invoiceId}`,
                    message: `Invoice #${emailForm.invoiceId} emailed, but ${missTotal} selected attachment${missTotal===1?' was':'s were'} missing on the server.`,
                    duration: 4500,
                    pill: true,
                  });
                }
              } catch {}
              try { showToast({ id: 'invoice.email.sent', message: `Invoice #${emailForm.invoiceId} emailed.`, duration: 2500, pill: true }); } catch {}
            } catch (err) {
              console.error(err);
              alert(err.message || 'Failed to send invoice email');
            } finally {
              setEmailSending(false);
            }
          }}
        />
      )}

      {/* ===== Confirm Bad Debt (Void) Modal ===== */}
      {voidConfirm.open && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-label="Mark Invoice as Bad Debt">
          <div className="modal">
            <h2><FaBomb style={{ marginRight: 8, color: '#dc2626' }} /> Mark Invoice as Bad Debt</h2>
            <div className="modal-scroll">
              <p>Are you sure you want to mark invoice #{voidConfirm.invoiceId} as bad debt? You can reopen it later if needed.</p>
            </div>
            <div className="modal-actions" style={{ justifyContent: 'flex-end' }}>
              <button className="btn" onClick={closeVoidConfirm}>Cancel</button>
              <button className="btn danger" onClick={confirmVoid}>Mark as Bad Debt</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== Invoice History Modal ===== */}
      {historyModal.open && (
        <div
          className="modal-overlay"
          onMouseDown={(e) => { if (e.target === e.currentTarget) closeInvoiceHistory(); }}
        >
          <div
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="invoiceHistoryTitle"
            onMouseDown={e => e.stopPropagation()}
            style={{ width: '640px', maxWidth: '90vw' }}
          >
            <div className="modal-header">
              <h2 id="invoiceHistoryTitle" style={{ margin: 0 }}>Invoice #{historyModal.invoiceId} History</h2>
              <button className="mini-btn" onClick={closeInvoiceHistory} title="Close" aria-label="Close">
                <FaTimes size={16} />
              </button>
            </div>
            <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              {historyModal.loading ? (
                <div style={{ padding: '12px 0' }}>Loading history…</div>
              ) : historyModal.entries.length ? (
                <div className="table-wrapper">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>User</th>
                        <th>Entry</th>
                      </tr>
                    </thead>
                    <tbody>
                      {historyModal.entries.map((entry) => (
                        <tr key={entry.id || `${entry.invoiceid}-${entry.date}-${entry.log}`}>
                          <td className="nowrap">{formatDateTime(entry.date)}</td>
                          <td className="nowrap">{entry.userFullName || 'System'}</td>
                          <td>{entry.log || ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ padding: '12px 0', color: '#666' }}>No history entries for this invoice.</div>
              )}
            </div>
            <div className="modal-actions" style={{ justifyContent: 'flex-end' }}>
              <button className="btn" onClick={closeInvoiceHistory}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== Email Multiple Invoices Modal ===== */}
      {emailBatchOpen && (
        <div className="modal-overlay">
          <div className="modal" role="dialog" aria-modal="true" aria-labelledby="emailBatchTitle">
            <h2 id="emailBatchTitle"><FaEnvelopeOpenText style={{ marginRight: 8 }}/> Email {emailBatchForm.ids.length} Invoices</h2>
            <form onSubmit={sendBatchEmails}>
              <div className="payment-form form-grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
                <label style={{ display:'inline-flex', alignItems:'center', gap:6, marginRight:16 }}>
                  <input
                    type="checkbox"
                    style={{ width:'16px', height:'16px' }}
                    checked={usePerInvoiceEmail}
                    onChange={e => { const v = e.target.checked; setUsePerInvoiceEmail(v); if (v) { setOverrideEmailTo(false); setSendAsMergedPdf(false); } }}
                  />
                  <span>Use each invoice's billing email (if available)</span>
                </label>

                <label style={{ display:'inline-flex', alignItems:'center', gap:6, marginRight:16 }}>
                  <input
                    type="checkbox"
                    style={{ width:'16px', height:'16px' }}
                    checked={sendAsMergedPdf}
                    onChange={e => {
                      const v = e.target.checked;
                      setSendAsMergedPdf(v);
                      if (v) { setUsePerInvoiceEmail(false); setOverrideEmailTo(true); }
                    }}
                  />
                  <span>Send as one email with merged PDF</span>
                </label>

                <label style={{ display:'inline-flex', alignItems:'center', gap:6, marginRight:16 }}>
                  <input
                    type="checkbox"
                    style={{ width:'16px', height:'16px' }}
                    checked={emailBatchSummarizeStorage}
                    onChange={e => setEmailBatchSummarizeStorage(e.target.checked)}
                  />
                  <span>Summarize storage charges (for storage invoices)</span>
                </label>

                <div style={{ display:'flex', flexDirection:'column', gap:4, marginRight:16, flex:1 }}>
                  <label style={{ display:'block', fontWeight:'600' }}>
                    <input
                      type="checkbox"
                      style={{ width:'16px', height:'16px', marginRight:6 }}
                      checked={overrideEmailTo}
                      onChange={e => { const v = e.target.checked; setOverrideEmailTo(v); if (v) setUsePerInvoiceEmail(false); }}
                    />
                    Override Email To
                  </label>
                  <input
                    type="text"
                    style={{ flex:'1 1 auto', minWidth:200 }}
                    value={emailBatchForm.to}
                    onChange={e => setEmailBatchForm(f => ({ ...f, to: e.target.value }))}
                    placeholder="recipient@example.com or a,b,c@example.com"
                    disabled={!overrideEmailTo}
                  />
                </div>

                <label>CC
                  <input
                    type="text"
                    value={emailBatchForm.cc}
                    onChange={e => setEmailBatchForm(f => ({ ...f, cc: e.target.value }))}
                    placeholder="comma-separated emails (optional)"
                  />
                </label>
                <label>BCC
                  <input
                    type="text"
                    value={emailBatchForm.bcc}
                    onChange={e => setEmailBatchForm(f => ({ ...f, bcc: e.target.value }))}
                    placeholder="comma-separated emails (optional)"
                  />
                </label>

                <label className="span-2">Subject (supports {'{invoiceId}'}, {'{clientName}'})
                  <input
                    type="text"
                    value={emailBatchForm.subject}
                    onChange={e => setEmailBatchForm(f => ({ ...f, subject: e.target.value }))}
                  />
                </label>

                <label className="span-2">Message (supports {'{invoiceId}'}, {'{clientName}'})
                  <textarea
                    rows={6}
                    style={{ resize:'vertical' }}
                    value={emailBatchForm.message}
                    onChange={e => setEmailBatchForm(f => ({ ...f, message: e.target.value }))}
                  />
                  <div style={{ fontSize: 12, opacity: .75, marginTop: 4 }}>
                    You can use {'{invoiceId}'} and {'{clientName}'} in the subject and message. When "Override Email To" is enabled, one email per invoice will be sent to the specified address.
                  </div>
                </label>
              </div>

              <div className="table-wrapper email-batch-table" style={{ marginTop: 12 }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Invoice #</th>
                      <th>Client</th>
                      <th>Billing Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    {emailBatchHeaders.map(h => (
                      <tr key={h.invoiceId}>
                        <td>#{h.invoiceId}</td>
                        <td className="nowrap">{h.clientName || ''}</td>
                        <td className="nowrap" title={h.email || ''}>{h.email || <em style={{ opacity:.7 }}>none</em>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="modal-actions" style={{ justifyContent:'flex-end' }}>
                <button type="button" className="btn" onClick={() => setEmailBatchOpen(false)}>Cancel</button>
                <button type="submit" className="btn primary" disabled={emailBatchSending}>
                  {emailBatchSending
                    ? 'Sending…'
                    : (sendAsMergedPdf
                        ? 'Send 1 Email'
                        : `Send ${emailBatchForm.ids.length} Email${emailBatchForm.ids.length===1?'':'s'}`)}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default Invoices;
